import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import moment from 'moment';
import keys from 'lodash/keys';
import differenceBy from 'lodash/differenceBy';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import round from 'lodash/round';
import memoize from 'lodash/memoize';
import negate from 'lodash/negate';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import zip from 'lodash/zip';
import sumBy from 'lodash/sumBy';
import pick from 'lodash/pick';
import keyBy from 'lodash/keyBy';

// Insights-specific utils
import {
  getOwnAttr,
  mapValues,
  nestedObjectDepth,
  nestedObjectKeys,
  nestedObjectSansOverall,
  sorted,
  sum,
} from 'common/util';
import {
  AbsoluteDateRange,
  getCurrentDate,
  localizedDate,
  MS_BY_UNIT,
  offsetForHighcharts,
  sortIsoDates,
} from 'common/util/date';
import {capitalize} from 'common/util/string';
import {
  DateRangeType,
  OrderedStandardTimeUnits,
  RelativeDate,
  StandardTimeUnit,
  TimeUnit,
} from 'common/widgets/date-range-picker/models/types';
import {TIME_COMPARISON_OPTIONS} from 'common/widgets/date-range-picker/models/presets';
import {
  ChartType,
  MATH_TYPE,
  PRIMITIVE_MATH_TYPES,
  PropertyType,
  REPORT_LIMITS,
  ReportName,
  ResourceType,
  RESULTS_OTHER_SUM_KEY,
  RESULTS_OVERALL_SUM_KEY,
  SPECIAL_EVENTS,
  ValueRepresentationType,
  XAU_MATH_TYPES,
  XAU_ONLY_MATH_TYPES,
} from 'common/report/constants';
import {getPresetMatch} from 'common/report/util';
import {
  AggregatedSeries,
  ChartPlotStyle,
  DisplayOptions,
  FlatOrColumnSortConfig,
  FlatSegment,
  GroupClause,
  InsightsBookmarkParams,
  InsightsBookmarkSections,
  InsightsBookmarkSortConfig,
  InsightsChartParams,
  InsightsQueryResults,
  InsightsResourceType,
  InsightsSeriesTree,
  InsightsSortConfig,
  ShowClause as ShowClauseType,
  TypeOfComparison,
} from 'common/report/insights/util/types';
import {BooleanFilterOperator, NumberOperator, StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {TrendDataRow} from 'common/components/data-table/trend-data/types';
import {formatDataTableNumber} from 'common/components/data-table/trend-data/utils';
import {GroupSortableTrendFields} from 'common/components/data-table/trend-data/constants';
import {
  CellRendererFunc,
  CommonDataTableFields,
  GroupSortableColumnsDictionary,
  SegmentColumnSpec,
  SegmentMetadataNode,
  TableColumn,
  TableColumnType,
  TableDataType,
  TableRow,
  TreeNodeType,
} from 'common/components/data-table/types';
import {reporter} from 'common/report/error-reporting';
import {ProjectTimezone} from 'common/report/stores/project-details';
import {LiftType, LiftTypeDescription} from 'common/report/util/lift/types';
import {propertiesUtils, renamePropertyValue} from 'common/report/util/properties';
import {getHeaderType, getPropertyTypeFromHeaderType, typedComparator} from 'common/report/util/sorting/header-sorting';
import {
  getDefaultSortConfig,
  getPrimaryValueField,
  updateSortingForNewNumSegments,
} from 'common/report/util/sorting/table-sorting';
import {
  FlatSortConfig,
  HeaderType,
  SortBy,
  SortConfig,
  SortOrder,
  UnorderedSumTree,
  UnorderedSumTreeNode,
} from 'common/report/util/sorting/types';
import {deepMapValuesToZero} from 'common/report/util/sorting/utils';
import {OrderBy} from 'common/util/array';
import {ChartTypeSelection} from 'common/widgets/chart-header/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {TOP_PEOPLE} from 'common/query-builder/query-block/properties-menu/constants';
import {ResultsKeysAndValues} from 'common/query-builder/query-report-header/types';
import {ForecastValue} from 'common/report/types';
import {
  isEventBasedBehavioralSegmentation,
  isMultiAttributionBehavior,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {RowTypeOption} from 'common/query-builder/query-entry-section/types';
import {MetricType} from 'common/query-builder/query-block/constants';
import {
  getBehaviorShowClauses,
  getExpandedBehaviorShowClauses,
  getFormulaShowClauses,
  getNumerOfEventComparisons,
  hasAnyOrderFunnel,
  hasCumulative,
  hasRolling,
  hasTTC,
  isBehaviorShowClause,
  isFormulaShowClause,
  isRetentionOnlyInsightsBookmark,
  isValidFormula,
} from 'common/report/insights/models/utils';
import {
  BehaviorShowClause,
  FormulaShowClause,
  ShowClause,
  ShowClauseClassType,
} from 'common/report/insights/models/show-clause';
import {Segmentation} from 'common/types/reports/query';
import {MATH_TO_FUNNEL_STEPS_METRIC} from 'common/widgets/alerts/custom-alert/constants';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import {getResolvedFormulaShowClause} from 'common/util/formulas';

import {END_OF_YESTERDAY, START_OF_TODAY, START_OF_YESTERDAY} from '../models/constants';
import {getNumberOfDaysInTimeClause, TimeClause} from '../models/time-clause';
import {ChartResultChartTypes} from './constants';
import {getValueIfLeaf} from './series';
import {getSelectedSegments} from './segments';

export {upsellOptions} from './upsell';

/**
 * Format a source (events, people, accounts, etc.) for title/label display
 * Uses sentence case
 * @param {string} source - the source string to be formatted
 * @param {object} [options]
 * @param {boolean} [options.singular=false] - de-pluralize the source
 * @param {boolean} [options.all=false] - prepend "all" (sets singular=false and article=false, uses title case)
 * @param {boolean} [options.article=false] - prepend "a" or "an" (sets singular=true)
 * @param {boolean} [options.forGroup=false] - prepend "for" (used for Cohorts)
 * @param {boolean} [options.groupBy=false] - prepend "group by"
 * @param {boolean} [options.compareTo=false] - prepend "compare to" (sets groupBy=false)
 * @param {boolean} [options.property=false] - append "property" or "properties" (depending on singular)
 * @param {object} [options.replace={}] - for each key, replace that string with the key's value
 * @param {boolean} [options.pluralizeDataGroup=false] - pluralize a data group name
 * @returns {string} - the formatted source string
 * @example
 *   formatSource(`events`, {singular: true}) -> `Event`
 *   formatSource(`people`, {singular: true}) -> `People`
 *   formatSource(`contacts`, {article: true}) -> `A contact`
 *   formatSource(`leads`, {groupBy: true, article: true, property: true}) -> `Group by a lead property`
 *   formatSource(`accounts`, {all: true}) -> `All Accounts`
 *   formatSource(`Company`, {all: true, pluralizeDataGroup: true}) -> `All Company(s)`
 */
// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function formatSource(
  source: string,
  {
    singular = false,
    all = false,
    article = false,
    forGroup = false,
    groupBy = false,
    compareTo = false,
    property = false,
    replace = {},
    pluralizeDataGroup = false,
  }: {
    singular?: boolean;
    all?: boolean;
    article?: boolean;
    forGroup?: boolean;
    groupBy?: boolean;
    compareTo?: boolean;
    property?: boolean;
    replace?: {[key: string]: string};
    pluralizeDataGroup?: boolean;
  } = {},
) {
  if (typeof source !== `string`) {
    throw new Error(`Invalid input: ${source}`);
  }

  let str = source;

  singular = article ? true : singular; // article sets singular=true
  singular = all ? false : singular; // all sets singular=false
  article = all ? false : article; // all sets article=false
  groupBy = compareTo ? false : groupBy; // compareTo sets groupBy=false

  // depluralize sources that end in "s"
  if ((singular || property) && source.length && source.endsWith(`s`)) {
    str = source.slice(0, -1);
  }

  // prepend "all" if requested
  if (all) {
    str = `all ${str}`;
  }

  // prepend "a"/"an" if requested
  if (article) {
    str = (source.length && `aeiou`.includes(source[0]) ? `an ` : `a `) + str;
  }

  // prepend "for" if requested
  if (forGroup) {
    str = `for ${str}`;
  }

  // prepend "group by" if requested
  if (groupBy) {
    str = `group by ${str}`;
  }

  // prepend "compare to" if requested
  if (compareTo) {
    str = `compare to ${str}`;
  }

  // append "(s)" to pluralize data group
  if (pluralizeDataGroup) {
    str = `${str}(s)`;
  }

  // append "property"/"properties" if requested
  if (property) {
    str = str + (singular ? ` property` : ` properties`);
  }

  // remove double spaces that can occur if empty string is passes as source
  str = str.replace(/\s+/, ` `);

  // "all" uses title case
  if (all) {
    str = str
      .split(` `)
      .map((word) => capitalize(word))
      .join(` `);
  }

  // replace
  Object.entries(replace).forEach(([original, replacement]) => {
    str = str.replace(original, replacement);
  });

  return capitalize(str);
}

export function isSpecialEvent(mpEvent) {
  return [SPECIAL_EVENTS.TOP_EVENTS, SPECIAL_EVENTS.ALL_EVENTS].includes(mpEvent.name);
}

// TODO(chi): move to common
export function mapObjectKeys(obj, callback) {
  const newObj = {};

  Object.keys(obj).forEach((key) => {
    newObj[key] = callback(obj[key]);
  });

  return newObj;
}

export function nestedObjectDepthInsightsFormat(obj) {
  return nestedObjectDepth(obj) - 1;
}

/**
 * Combine nested objected into one object of keys and the sum of their numeric values
 * @example
 * combineNestedObjKeys({
 *   first: {foo: 1, bar:2},
 *   second: {foo: 4, bar:1, tab: 2},
 * });
 * // {foo: 5, bar: 3, tab: 2}
 */
export function combineNestedObjKeys(obj, accum = {}) {
  if (Object.values(obj).some((v) => typeof v === `number`)) {
    Object.entries(obj).forEach(([k, v]) => {
      accum[k] = accum[k] ? accum[k] + v : v;
    });
  } else {
    Object.keys(obj).map((key) => combineNestedObjKeys(obj[key], accum));
  }
  return accum;
}

export function nestedObjectSumLeafInsightsFormatTimeSeries(obj) {
  if (isObject(obj.value)) {
    return {
      value: sum(Object.values(obj.value)),
      ...(isObject(obj.timeComparisonValue) ? {timeComparisonValue: sum(Object.values(obj.timeComparisonValue))} : {}),
    };
  } else {
    return Object.fromEntries(
      Object.keys(obj).map((key) => [key, nestedObjectSumLeafInsightsFormatTimeSeries(obj[key])]),
    );
  }
}

export function nestedObjectSumLeafInsightsFormat(
  obj,
  {shouldDisplayTimeComparison = false, shouldRetrieveSegment = false, shouldDisplayLiftComparison = false} = {},
) {
  if (Object.keys(obj).some((key) => isFinite(obj[key].value))) {
    // leaf
    const retObj = {
      value: Object.values(obj).reduce((accum, val) => accum + (val as any).value, 0),
    };
    if (shouldDisplayTimeComparison) {
      retObj[`timeComparisonValue`] = Object.values(obj).reduce(
        (accum, val) => accum + (val as any).timeComparisonValue,
        0,
      );
    }
    if (shouldDisplayLiftComparison) {
      retObj[`liftComparisonValue`] = Object.values(obj).reduce(
        (accum, val) => accum + (val as any).liftComparisonValue,
        0,
      );
    }
    if (shouldRetrieveSegment) {
      retObj[`segment`] = Object.values(obj).reduce((_accum, val) => (val as any).segment, ``);
    }
    return retObj;
  } else {
    return Object.fromEntries(
      Object.keys(obj).map((key) => [
        key,
        nestedObjectSumLeafInsightsFormat(obj[key], {
          shouldDisplayTimeComparison,
          shouldRetrieveSegment,
          shouldDisplayLiftComparison,
        }),
      ]),
    );
  }
}

/**
 * Sum the leaf values of a nested object,
 * constructing a new object with depth 1 less than the original
 */
export function nestedObjectSumLeaf(obj) {
  if (Object.values(obj).some((val) => typeof val === `object`)) {
    return Object.fromEntries(Object.keys(obj).map((key) => [key, nestedObjectSumLeaf(obj[key])]));
  }
  // @ts-ignore Operator '+' cannot be applied to types 'unknown' and 'unknown'.ts(2365)
  return Object.values(obj).reduce((accum, val) => accum + val, 0);
}

export function callbackIntoObject(obj, callback, {minDepth = 0, depthFirst = false} = {}) {
  const depth = nestedObjectDepthInsightsFormat(obj);
  const shouldContinue = depth >= minDepth;
  const objKeys = Object.keys(obj);

  if (!depthFirst) {
    objKeys.forEach((key) => shouldContinue && callback(key, obj, depth));
  }
  if (shouldContinue) {
    objKeys.forEach((key) => {
      if (typeof obj[key] === `object`) {
        callbackIntoObject(obj[key], callback, {minDepth, depthFirst});
      }
    });
  }
  if (depthFirst) {
    objKeys.forEach((key) => shouldContinue && callback(key, obj, depth));
  }
}

/**
 * Turn a nested object into a list of "path" arrays,
 * which represent all of its key combinations.
 * Example:
 *   {                     [['a', 'x', 1],
 *     a: {x: 1, y: 2}, =>  ['a', 'y', 2],
 *     b: {x: 1, y: 2},     ['b', 'x', 1],
 *   }                      ['b', 'y', 2]]
 * The depth param controls how deep into the object the
 * transformation is applied. Sub-objects of the given
 * depth will be placed at the last index of the path.
 * Example (depth=1):
 *   {                     [['a', {x: 1}],
 *     a: {x: 1, y: 2}, =>  ['a', {y: 2}],
 *     b: {x: 1, y: 2},     ['b', {x: 1}],
 *   }                      ['b', {y: 2}]]
 */
export function nestedObjectPaths(obj, depth = 0) {
  const paths = [];

  function _getObjectPaths(obj, path = []) {
    const objDepth = nestedObjectDepthInsightsFormat(obj);
    if (objDepth > depth) {
      Object.keys(obj).forEach((key) => _getObjectPaths(obj[key], [...path, key]));
    } else {
      paths.push([...path, obj]);
    }
  }

  _getObjectPaths(obj);

  return paths;
}

/**
 * @deprecated
 *
 * DEPRECATED: use flattenSeries() from "common/report/insights/util.ts" instead
 *
 * Flatten a nested object to show all possible paths + corresponding values of that object
 * @param {string} obj - nested object to be flattened
 * @returns {object} with all nested keys seperated by spaces
 * @example
 * flattenNestedObjectToPath({'US': {'California': 1, 'New York' : 2}});
 * // {
 * //   values: {'US California': 1, 'US New York': 2},
 * //   paths: {{'US California': ['US', 'California'], 'US New York': ['US', 'New York']},
 * // }
 */
export function flattenNestedObjectToPath(
  series,
  {flattenValues = false, isTimeSeriesData = false, formatHeader = null, shouldDisplayTimeComparison = false} = {},
) {
  const paths = {};
  let values = nestedObjectPaths(series, 1).reduce((values, headers) => {
    const results = headers.pop();
    const key = formatHeader ? formatHeader(headers) : headers.join(` `);
    paths[key] = headers;
    return Object.assign(values, {[key]: results});
  }, {});
  if (flattenValues) {
    values = isTimeSeriesData
      ? nestedObjectSumLeafInsightsFormatTimeSeries(values)
      : nestedObjectSumLeafInsightsFormat(values, {shouldDisplayTimeComparison});
    values = nestedObjectSumLeaf(values);
  }
  return {paths, values};
}

/**
 * Find all reachable nodes in an object from a starting key at a depth
 * @param {Object} options
 * @param {Object} options.series - The object searched
 * @param {Object[]} options.keysToMatch - A list of keys that are considered descendents
 * @param {String} options.depth - The depth at which keysToMatch exists
 * @returns {object} returns a nested object of the matching ancestors set to true inside of
 * the depth which they were found
 * @example
 * reachableNodesOfKey({
 *   series: {'US': {'California': {'San Francisco': 2}, 'New York' : {'Buffalo': 1}}},
 *   keysToMatch: ['California'],
 *   depth: 1,
 * });
 * //{1: {'San Francisco': true}, 2: {'California': true}, 3: {'US': true}}
 */
export function reachableNodesOfKey({series = {}, keysToMatch = [], depth = 1} = {}) {
  const REACHABLE_NODES = {
    [depth]: keysToMatch.reduce((obj, key) => {
      obj[key] = true;
      return obj;
    }, {}),
  };
  const _addToFamily = (depth, key) => {
    REACHABLE_NODES[depth] = REACHABLE_NODES[depth] || {};
    REACHABLE_NODES[depth][key] = true;
  };
  callbackIntoObject(
    series,
    (value, objectHoldingValue, depthInSeries) => {
      if (depthInSeries > depth) {
        // create ancestor list
        const valueChilden = Object.keys(objectHoldingValue[value]);
        const keysToMatch = Object.keys(REACHABLE_NODES[depthInSeries - 1] || {});
        const objectHasMatch = keysToMatch.some((matchKey) => valueChilden.includes(matchKey));
        if (objectHasMatch) {
          _addToFamily(depthInSeries, value);
        }
      } else if (depthInSeries === depth && keysToMatch.includes(value)) {
        // add descendents of matching keys
        callbackIntoObject(
          objectHoldingValue[value],
          (childValue, _, childDepth) => {
            _addToFamily(childDepth, childValue);
          },
          {minDepth: 2},
        ); // remove time series data
      }
    },
    {
      minDepth: depth,
      depthFirst: true,
    },
  );

  return REACHABLE_NODES;
}

// TODO Jordan: move offsetTimestampWithDst + createBaseResults to DATE UTILS in common and add tests

/**
 * Turn a project based timestamp into local time accounting for DST
 * @param {Number} timestamp - the timestamp in project time to be converted
 * @returns {Number} converted time
 * @example
 * offsetTimestampWithDst(1490140800000); // Done in PST timezone
 * // 1490166000000
 */
export function offsetTimestampWithDst(timestamp) {
  // If we are currently in DST and the timestamp in local time is NOT in DST add an hour to offset it.
  const date = moment(timestamp);
  if (moment().isDST() && !date.isDST()) {
    date.add(1, `hours`);
  }
  return date.valueOf();
}

/**
 * Provide base results object for all timestamps between toDate and fromDate. from / to falls back to the results min and max.
 * @param {Array} results - jql result object to get dates from. This assumes that the last item in the result is the timestamp/
 * @param {Number} [toDate] - starting date for zeroing range. only applies if unit is provided.
 * @param {Number} [fromDate] - ending date for zeroing range. only applies if unit is provided.
 * @param {Boolean} [timestampIsSeconds] - Converts result timestamps to MS by multiplying 1000 if this is set to true.
 * @param {String} [unit] - if unit is provided dates for  fromDate -> toDate are included in the results
 * @returns {object} with all nested keys seperated by spaces
 * @example
 * flattenNestedObjectToPath([
    {"key":["Run query",1489968000000],"value":922},
    {"key":["Run query",1490140800000],"value":505}
  ], {toDate=1490140800000, fromDate=1489968000000, timestampIsSeconds=false, unit='day'});
 * // {
 * //   1489993200000: 0,
 * //   1490079600000: 0,
 * //   1490166000000: 0,
 * // }
 */
export function createBaseResults(
  results,
  {toDate = null, fromDate = null, timestampIsSeconds = false, unit = null} = {},
) {
  const sortedTimestamps = results
    .map((result) => result.key[result.key.length - 1])
    .filter((timestamp) => timestamp !== null && Number.isInteger(Number(timestamp)))
    .map((timestamp) => (timestampIsSeconds ? Number(timestamp) * 1000 : Number(timestamp)))
    .sort();

  const minTimestamp = sortedTimestamps[0];
  const maxTimestamp = sortedTimestamps[sortedTimestamps.length - 1];

  const timestampsForRange = [];
  if (unit) {
    const fromMoment = moment(fromDate || minTimestamp);
    const toMoment = moment(toDate || maxTimestamp);

    // @ts-ignore
    for (let cursor = fromMoment; cursor.isBefore(toMoment); cursor.add(1, `${unit}s`)) {
      timestampsForRange.push(cursor.valueOf());
    }
  }

  return sortedTimestamps
    .concat(timestampsForRange)
    .reduce((obj, timestamp) => Object.assign(obj, {[timestamp]: 0}), {});
}

/**
 * Returns a copy of the array provided with each item seperated by a new string given as the second argument
 * @param arr {array}
 * @param str {string}
 * @returns {array}
 * @example
 * joinAsList([`one`, `two`, `three`], `and`)
 * // [`one`, `and`, `two`, `and`, `three`]
 */
export function joinAsList(arr, str) {
  const arrCopy = [...arr];
  return arrCopy.length ? [arrCopy.shift()].concat(...arrCopy.map((i) => [str, i])) : [];
}

export function convertTimeClauseValuesToTimeRange(timeClause, utcOffset) {
  let from, to;
  let {dateRangeType, unit, value, window} = timeClause;
  const now = getCurrentDate().valueOf();
  const convertTime = (timeInput) => {
    if (typeof timeInput === `string`) {
      return moment.utc(timeInput).utcOffset(utcOffset).subtract(utcOffset, `minutes`);
    }
    return moment.utc(timeInput).utcOffset(utcOffset);
  };
  switch (dateRangeType) {
    case DateRangeType.On:
    case DateRangeType.Between: {
      const [fromStr, toStr] = value;
      if (fromStr === START_OF_YESTERDAY && toStr === END_OF_YESTERDAY) {
        const yesterdayStr = convertTime(now).subtract(1, `days`).format(`YYYY-MM-DD`);
        from = convertTime(yesterdayStr);
        to = convertTime(yesterdayStr).add(1, `days`).subtract(1, `minutes`);
      } else {
        from = convertTime(fromStr);
        to = convertTime(toStr).add(1, `days`).subtract(1, `minutes`);
      }
      break;
    }
    case DateRangeType.Since: {
      to = convertTime(now);
      let fromStr;
      if (value === START_OF_TODAY) {
        fromStr = to.clone().format(`YYYY-MM-DD`);
      } else {
        [fromStr] = value;
      }
      from = convertTime(fromStr);
      break;
    }
    case DateRangeType.RelativeAfter: {
      to = convertTime(now);
      ({unit, value} = window);
      from = to.clone().subtract(value, unit);
      break;
    }
    default: {
      to = convertTime(now);
      from = to.clone().subtract(value, unit);
      break;
    }
  }
  return {from, to};
}

export function getNumDaysApart(timeClause, utcOffset) {
  const {from, to} = convertTimeClauseValuesToTimeRange(timeClause, utcOffset);
  return moment(to.format(`YYYY-MM-DD`)).diff(from.format(`YYYY-MM-DD`), `days`);
}

export function getOffsetTimeRange({unit, value, timeClause, utcOffset}) {
  const {from, to} = convertTimeClauseValuesToTimeRange(timeClause, utcOffset);
  const offsetFrom = from.clone().subtract(value, unit);
  const numSecondsApart = to.diff(from, `seconds`);
  const offsetTo = offsetFrom.clone().add(numSecondsApart, `seconds`);
  return {offsetFrom, offsetTo};
}

export function getTimeComparisonTrackingData({timeClause, utcOffset, timeComparison}) {
  const {from, to, offset} = timeComparison;
  let offsetDays;
  if (offset) {
    const {unit, value} = offset;
    if (unit === `days`) {
      offsetDays = value;
    } else {
      const {from} = convertTimeClauseValuesToTimeRange(timeClause, utcOffset);
      const offsetFrom = from.clone().subtract(value, unit);
      offsetDays = from.diff(offsetFrom, `days`);
    }
  }
  const presetMatch = getPresetMatch(timeComparison);
  return {
    option: presetMatch ? presetMatch.label : TIME_COMPARISON_OPTIONS.CUSTOM,
    ...(isNumber(offsetDays) ? {offset_days: offsetDays} : {}),
    ...(from ? {from_date: from} : {}),
    ...(to ? {to_date: to} : {}),
  };
}

// Most trivial way to rename `people` to `users` within insights.
// Currently the insights API sends back formatted names like 'All people - Total',
// But we also need to support the enums used like $people and $all_people.
// TODO: refactor API and iron code to be consistent with `user` naming and delete this.
const REPLACE_PEOPLE = {people: `users`, People: `Users`};
export function formatPeople(source) {
  Object.entries(REPLACE_PEOPLE).forEach(([original, replacement]) => {
    source = source.replace(original, replacement);
  });
  return source;
}

/** Compares time units by length. Returns less than 0 if a < b, 0 if equal, and greater than 0 if a > b (like an Array#sort compareFunction).
 * @example
 * compareTimeUnits(`minute`, `day`) // => -2
 */
export function compareTimeUnits(a: StandardTimeUnit, b: StandardTimeUnit): number {
  return OrderedStandardTimeUnits.indexOf(a) - OrderedStandardTimeUnits.indexOf(b);
}

export const XAU_TO_TIME_UNIT = {
  [XAU_MATH_TYPES.DAU]: TimeUnit.Day,
  [XAU_MATH_TYPES.WAU]: TimeUnit.Week,
  [XAU_MATH_TYPES.MAU]: TimeUnit.Month,
};

export function getKeysAtDepth(series: InsightsSeriesTree, targetDepth, currentDepth = 0, acc = []): string[] {
  if (!series) {
    return acc;
  }
  if (currentDepth >= targetDepth) {
    return acc.concat(keys(series));
  } else {
    const nextDepth = currentDepth + 1;
    // Omit the $overall key when looking at possible baseline values; it lives at a higher level than the other breakdowns so we get
    // some date timestamps if we don't omit this
    const seriesWithoutOverall: Array<[string, InsightsSeriesTree]> = Object.entries(series).filter(
      ([seriesKey]) => seriesKey !== RESULTS_OVERALL_SUM_KEY,
    );
    return acc.concat(
      seriesWithoutOverall.reduce((currKeys, [_, childSeries]) => {
        return currKeys.concat(getKeysAtDepth(childSeries, targetDepth, nextDepth));
      }, []),
    );
  }
}

export const STRING_OPERATOR_NEGATION_MAP = {
  [StringOperator.Contains]: StringOperator.DoesNotContain,
  [StringOperator.DoesNotContain]: StringOperator.Contains,
  [StringOperator.Equals]: StringOperator.DoesNotEqual,
  [StringOperator.DoesNotEqual]: StringOperator.Equals,
  [StringOperator.Set]: StringOperator.NotSet,
  [StringOperator.NotSet]: StringOperator.Set,
};

export const NUMBER_OPERATOR_NEGATION_MAP = {
  [NumberOperator.Between]: NumberOperator.NotBetween,
  [NumberOperator.NotBetween]: NumberOperator.Between,
  [NumberOperator.AtLeast]: NumberOperator.LessThan,
  [NumberOperator.LessThan]: NumberOperator.AtLeast,
  [NumberOperator.AtMost]: NumberOperator.GreaterThan,
  [NumberOperator.GreaterThan]: NumberOperator.AtMost,
  [NumberOperator.EqualTo]: NumberOperator.NotEqualTo,
  [NumberOperator.NotEqualTo]: NumberOperator.EqualTo,
};

export const BOOLEAN_OPERATOR_NEGATION_MAP = {
  [BooleanFilterOperator.True]: BooleanFilterOperator.False,
  [BooleanFilterOperator.False]: BooleanFilterOperator.True,
};

export function isCohortsQuery({
  params,
  metricsResource,
}: {
  params: InsightsBookmarkParams;
  metricsResource: MetricsResource;
}) {
  return getExpandedBehaviorShowClauses({showClauses: params.sections.show, metricsResource}).some(
    (showElement) => showElement.behavior.resourceType === InsightsResourceType.Cohorts,
  );
}

/**
 * Process insights results for a heuristic whether we've hit the api segmentation limit.
 * TODO: this would be more accurate and perform better if implemented in arb.
 */
export function addQueryLimitInfo(
  results: InsightsQueryResults,
  limit = REPORT_LIMITS[ReportName.Insights].query,
): InsightsQueryResults {
  const targetDepth = results.headers.length - 2;

  const reportSection = results.meta.report_sections.show || results.meta.report_sections.formula || [];

  reportSection.forEach((collection) => {
    // report_sections: {show: [ {sub_queries: [ {metric_key:"Event Name"} ] } ] }
    // or report_sections: {show: [ {metric_key:"Event Name"} ] }
    const metrics = collection.sub_queries || [collection];

    metrics.forEach((metric) => {
      if (!metric.metric_key) {
        // sometimes not a metric_key (no results for that metric)
        return; // continue forEach
      }
      let series = results.series[metric.metric_key];
      // in a timeComparison scenario, sometimes there's only data in the timComparison series
      if (!series && results.time_comparison?.series) {
        series = results.time_comparison.series[metric.metric_key];
      }

      // inferring that there is more data beyond the query limit
      const keyCount = getKeysAtDepth(series as InsightsSeriesTree, targetDepth).length;
      metric.apiResultsTruncated = keyCount >= limit;
      if (metric.apiResultsTruncated) {
        results.meta.apiResultsTruncated = true;
      }
    });
  });

  // augmenting with the limits applied to the query, to be used in informing the user of truncation
  results.meta.queryLimits = {limit};

  return results;
}

export const NUMERIC_DATA_PRECISION = 4;
export const DEFAULT_VISIBLE_SEGMENTS = 12;

function getTimestampsHelper(obj: Obj, objectDepth: number): number[] {
  if (objectDepth === 1) {
    return Object.keys(obj)
      .map((ts) => Number(ts))
      .sort();
  }
  return getTimestampsHelper(nestedObjectSansOverall(obj), objectDepth - 1);
}

export function getTimestamps(series: Obj): number[] {
  if (isEmpty(series)) {
    return [];
  }
  return getTimestampsHelper(series, nestedObjectDepth(series));
}

export function mergeTimestamps({
  series,
  timeComparisonSeries,
  offsetMs,
  isTimeSeriesData,
  liftComparisonSeries,
}): {
  timestamps: number[];
  timeComparisonTimestamps: number[];
  liftComparisonTimestamps: number[];
} {
  if (!isTimeSeriesData || (isEmpty(series) && isEmpty(timeComparisonSeries) && isEmpty(liftComparisonSeries))) {
    return {timestamps: [], timeComparisonTimestamps: [], liftComparisonTimestamps: []};
  }

  // If at least one of the series is not empty, we expects to generate at least one timestamp list

  let timestamps = getTimestamps(series);
  let timeComparisonTimestamps = getTimestamps(timeComparisonSeries);
  let liftComparisonTimestamps = getTimestamps(liftComparisonSeries);
  if (offsetMs) {
    if (isEmpty(timestamps)) {
      timestamps = timeComparisonTimestamps.map((timestamp) => Number(timestamp) + offsetMs);
    }
    if (isEmpty(timeComparisonTimestamps)) {
      timeComparisonTimestamps = timestamps.map((timestamp) => Number(timestamp) - offsetMs);
    }
    if (isEmpty(liftComparisonTimestamps)) {
      liftComparisonTimestamps = timestamps.map((timestamp) => Number(timestamp) + offsetMs);
    }
  }
  return {timestamps, timeComparisonTimestamps, liftComparisonTimestamps};
}

function isKeyExistInObject({obj, key}) {
  return isObject(obj) && Object.prototype.hasOwnProperty.call(obj, key);
}

function extractSeriesLeafValue({series, key, nullValue}) {
  let value = isKeyExistInObject({obj: series, key}) ? series[key] : nullValue;

  if (isNil(value)) {
    // This is to handle a backend bug where it sometimes return `null` values.
    value = nullValue;
  }

  if (typeof value === `number`) {
    return roundInsightsValue(value);
  }

  return value;
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
function generateSeriesFromInsightsAPIResponseSeriesHelper({
  series,
  timestamps,
  liftComparisonSeries,
  liftComparisonTimestamps,
  timeComparisonSeries,
  timeComparisonTimestamps,
  nullValue,
  isTimeSeriesData = false,
  objectDepth,
}) {
  const seriesKeys = isObject(series) ? Object.keys(series) : [];
  const timeComparisonSeriesKeys = isObject(timeComparisonSeries) ? Object.keys(timeComparisonSeries) : [];
  const liftComparisonSeriesKeys = isObject(liftComparisonSeries) ? Object.keys(liftComparisonSeries) : [];
  const mergedKeys = [...new Set([...seriesKeys, ...timeComparisonSeriesKeys, ...liftComparisonSeriesKeys])];
  const newSeries = {};

  mergedKeys.forEach((key) => {
    if (objectDepth === 1) {
      let value = extractSeriesLeafValue({series, key, nullValue});
      let timeComparisonValue = extractSeriesLeafValue({series: timeComparisonSeries, key, nullValue});
      let liftComparisonValue = extractSeriesLeafValue({series: liftComparisonSeries, key, nullValue});
      if (isTimeSeriesData) {
        if (!isObject(value)) {
          value = Object.fromEntries(timestamps.map((key) => [key, nullValue]));
        }
        if (isObject(timeComparisonSeries) && !isObject(timeComparisonValue)) {
          timeComparisonValue = Object.fromEntries(timeComparisonTimestamps.map((key) => [key, nullValue]));
        }
        if (isObject(liftComparisonSeries) && !isObject(liftComparisonValue)) {
          liftComparisonValue = Object.fromEntries(liftComparisonTimestamps.map((key) => [key, nullValue]));
        }
      }
      newSeries[key] = {
        value,
        ...(timeComparisonSeries === null ? {} : {timeComparisonValue}),
        ...(liftComparisonSeries === null ? {} : {liftComparisonValue}),
      };
    } else if (key === RESULTS_OVERALL_SUM_KEY) {
      // series could be {}
      // because there may have been activity for that segment in a past period, but not in this period
      const overallNode = isKeyExistInObject({obj: series, key}) ? series[key] : {};
      newSeries[key] = newSeries[key] || {};

      Object.keys(overallNode).forEach((childKey) => {
        const value = overallNode[childKey];
        let timeComparisonValue;
        if (timeComparisonSeries) {
          // the "prior" series is not guaranteed to have all of the same keys/data points
          // e.g. if there was no activity in a given country, and we're segmented by country
          timeComparisonValue = timeComparisonSeries.key?.childKey || 0;
        }

        // liftComparison $overall wouldn't make sense to carry over,
        // as it will always be the same as the main series, so not adding here
        newSeries[key][childKey] = {
          value,
          timeComparisonValue,
        };
      });
    } else {
      const innerSeries = isKeyExistInObject({obj: series, key}) ? series[key] : {};
      let innerTimeComparisonSeries = null;
      let innerLiftComparisonSeries = null;
      if (timeComparisonSeries) {
        innerTimeComparisonSeries = isKeyExistInObject({obj: timeComparisonSeries, key})
          ? timeComparisonSeries[key]
          : {};
      }
      if (liftComparisonSeries) {
        innerLiftComparisonSeries = isKeyExistInObject({obj: liftComparisonSeries, key})
          ? liftComparisonSeries[key]
          : {};
      }

      newSeries[key] = generateSeriesFromInsightsAPIResponseSeriesHelper({
        isTimeSeriesData,
        nullValue,
        series: innerSeries,
        timestamps,
        timeComparisonSeries: innerTimeComparisonSeries,
        timeComparisonTimestamps,
        objectDepth: objectDepth - 1,
        liftComparisonSeries: innerLiftComparisonSeries,
        liftComparisonTimestamps,
      });
    }
  });
  return newSeries;
}

/**
 * @deprecated - only used for the old table-chart
 */
export function generateSeriesFromInsightsAPIResponseSeries({
  series,
  timeComparisonSeries = null,
  nullValue = 0,
  offsetMs = null,
  isTimeSeriesData = false,
  liftComparisonSeries = null,
}) {
  const objectDepth = nestedObjectDepth(series) + (isTimeSeriesData ? -1 : 0);
  const {timestamps, timeComparisonTimestamps, liftComparisonTimestamps} = mergeTimestamps({
    series,
    timeComparisonSeries,
    offsetMs,
    isTimeSeriesData,
    liftComparisonSeries,
  });
  return generateSeriesFromInsightsAPIResponseSeriesHelper({
    isTimeSeriesData,
    nullValue,
    objectDepth,
    series,
    timestamps,
    timeComparisonSeries,
    timeComparisonTimestamps,
    liftComparisonSeries,
    liftComparisonTimestamps,
  });
}

export function roundInsightsValue(value: number) {
  return round(value, NUMERIC_DATA_PRECISION);
}

export function getTimeComparisonOffsetMs({dateRange, timeComparisonDateRange}) {
  const fromDateMoment = moment.utc(dateRange.fromDate);
  const timeComparisonFromDateMoment = moment.utc(timeComparisonDateRange.fromDate);
  return fromDateMoment.diff(timeComparisonFromDateMoment, `milliseconds`);
}

/**
 * Computes the relative change from the previous value to the current value.
 * @param {Number} currVal - current value
 * @param {Number} prevVal - previous value
 * @returns {Number} relative change from prevVal to currVal
 */
export function computeDelta(currVal, prevVal) {
  if (!isFinite(currVal) || !isFinite(prevVal) || prevVal === 0) {
    return null;
  }
  return (currVal - prevVal) / Math.abs(prevVal);
}

// region Helper functions for formatDateRanges

function getDateFormatConfig({fromDate, toDate, currentDate}) {
  const fromMoment = moment.parseZone(fromDate);
  const toMoment = moment.parseZone(toDate);
  const startOfDayFromMoment = fromMoment.clone().startOf(`day`);
  const startOfDayToMoment = toMoment.clone().startOf(`day`);
  const endOfDayToMoment = toMoment.clone().endOf(`day`);
  let isMultiDay = true;
  let isPartialDay = true;
  if (fromMoment.isSame(startOfDayFromMoment) && toMoment.isSame(endOfDayToMoment)) {
    if (fromMoment.isSame(startOfDayToMoment)) {
      isMultiDay = false;
    }
    isPartialDay = false;
  }

  let includeYear = true;
  // NOTE: This could be slightly inaccurate because we're not considering using utcOffset
  const currentYear = moment(currentDate).year();
  if (fromMoment.year() === currentYear && toMoment.year() === currentYear) {
    includeYear = false;
  }

  return {isMultiDay, isPartialDay, includeYear};
}

function formatSingleDate(date, {isPartialDay, includeYear}) {
  const DATE_FORMAT = `MMM D`;
  const TIME_FORMAT = `h:mma`;
  const dateMoment = moment.parseZone(date);
  let dateStr = `${dateMoment.format(DATE_FORMAT)}`;
  if (includeYear) {
    dateStr = `${dateStr} ${dateMoment.year()}`;
  }
  if (isPartialDay) {
    dateStr = `${dateStr}, ${dateMoment.format(TIME_FORMAT)}`;
  }
  return dateStr;
}

export function formatDateRange(
  {fromDate, toDate},
  {isPartialDay = false, includeYear = false, isMultiDay = false} = {},
) {
  const formattedFromDate = formatSingleDate(fromDate, {isPartialDay, includeYear});
  if (isMultiDay) {
    const formattedToDate = formatSingleDate(toDate, {isPartialDay, includeYear});
    return `${formattedFromDate} - ${formattedToDate}`;
  } else {
    return formattedFromDate;
  }
}

export function formatDateRanges(
  dateRanges: AbsoluteDateRange[],
  {currentDate = getCurrentDate()}: {currentDate?: Date | moment.Moment} = {},
): string[] {
  const dateFormatConfigs = dateRanges.map(({fromDate, toDate}) => {
    return getDateFormatConfig({fromDate, toDate, currentDate});
  });

  const mergedDateFormatConfig = {};
  Object.keys(dateFormatConfigs[0]).map((configKey) => {
    mergedDateFormatConfig[configKey] = dateFormatConfigs.some((config) => config[configKey]);
  });

  return dateRanges.map((dateRange) => formatDateRange(dateRange, mergedDateFormatConfig));
}

// endregion

/**
 * Returns true if the first time-period has not been completed
 */
export function isIncompleteFirstInterval(
  firstTimestamp: number,
  beginningDate: string,
  {mathOperator, utcOffset = 0}: {mathOperator: MATH_TYPE; utcOffset: number},
): boolean {
  if ((Object.values(XAU_MATH_TYPES) as MATH_TYPE[]).includes(mathOperator)) {
    return false;
  }

  const localizedFirstTimestampDate = new Date(firstTimestamp);
  const localizedBeginningDate = localizedDate({utcOffset, date: new Date(beginningDate)});

  const threshold = MS_BY_UNIT[`hour`]; // add a threshold of 1 hour to account for daylight saving
  return Math.abs(localizedBeginningDate.getTime() - localizedFirstTimestampDate.getTime()) > threshold;
}

/**
 * Returns true if the last time-period has not been completed
 */
export function isIncompleteLastInterval(
  lastTimestamp: number,
  endingDate: string,
  {mathOperator, unit, utcOffset}: {mathOperator: MATH_TYPE; unit: string; utcOffset: number},
): boolean {
  if (!lastTimestamp || !unit) {
    return false;
  }
  if ((Object.values(XAU_MATH_TYPES) as MATH_TYPE[]).includes(mathOperator) && mathOperator !== XAU_MATH_TYPES.DAU) {
    // XAU queries always display as a complete last interval, except DAU
    return false;
  }

  // Calculate interval from the last timestamp to the end of the time range
  const localizedEndingDate = localizedDate({utcOffset, date: new Date(endingDate)});
  const localizedLastTimestampDate = new Date(lastTimestamp);
  const currentInterval = Math.abs(localizedEndingDate.getTime() - localizedLastTimestampDate.getTime());

  // Calculate interval length
  let timeInterval = null,
    start = null,
    end = null;
  switch (unit.toLowerCase()) {
    case `minute`:
    case `hour`:
    case `day`:
    case `week`:
    case `month`:
      timeInterval = MS_BY_UNIT[unit.toLowerCase()];
      break;
    case `quarter`:
      end = new Date(localizedEndingDate.getFullYear(), localizedEndingDate.getMonth() + 1);
      start = new Date(localizedEndingDate.getFullYear(), localizedEndingDate.getMonth() - 2);
      timeInterval = end.getTime() - start.getTime();
      break;
    default:
      console.error(`Unknown interval: "${timeInterval}"`);
      return false;
  }
  const threshold = MS_BY_UNIT[`hour`] + 10 * MS_BY_UNIT[`minute`]; // 1 hour to account for daylight saving, 10 mins because day end at 23:50
  return timeInterval - currentInterval > threshold;
}

function getSupportedTimeUnitsForXauMathType(mathType: XAU_MATH_TYPES): TimeUnit[] {
  switch (mathType) {
    case XAU_MATH_TYPES.DAU:
      return [TimeUnit.Day];
    case XAU_MATH_TYPES.WAU:
      return [TimeUnit.Day, TimeUnit.Week];
    case XAU_MATH_TYPES.MAU:
      return [TimeUnit.Day, TimeUnit.Week, TimeUnit.Month];

    default: {
      const unreachable: never = mathType; // a trick to enforce full enum coverage in switch case
      throw new Error(`unknown XAU math type "${unreachable}"`);
    }
  }
}

// this is an internal interface that should never be exported outside common utils
interface TimeUnitRestriction {
  supportedUnits: Set<TimeUnit>;

  // the reason given for why time units that are not supported by "supportedUnits" are restricted
  getRestrictionReason: (unit: TimeUnit) => string;
}

// this is an internal method that should never be exported outside common utils
function getSupportedTimeUnitRestrictions(bookmarkParams: InsightsBookmarkParams): TimeUnitRestriction[] {
  const showClauses = bookmarkParams.sections.show;
  const behaviorShowClauses = getBehaviorShowClauses(showClauses);
  const groupClauses = bookmarkParams.sections.group ?? [];
  const filterClauses = bookmarkParams.sections.filter ?? [];
  const [timeClause] = bookmarkParams.sections.time;
  const availableUnits = Object.values(TimeUnit);
  const restrictions: TimeUnitRestriction[] = [];

  const hasCohortsOverTime = behaviorShowClauses.some(
    (clause) =>
      clause.behavior.resourceType === InsightsResourceType.Cohorts ||
      clause.behavior.resourceType === InsightsResourceType.People,
  );

  const hasNonAttributionBehavioralSegmentation = groupClauses.some((group) => {
    return `behavior` in group && !isMultiAttributionBehavior(group.behavior);
  });
  const hasBehavioralFilters = filterClauses.some((group) => `behavior` in group);

  if (hasCohortsOverTime || hasNonAttributionBehavioralSegmentation || hasBehavioralFilters) {
    const supportedUnits = new Set<TimeUnit>(availableUnits);
    supportedUnits.delete(TimeUnit.Minute);
    supportedUnits.delete(TimeUnit.Hour);

    restrictions.push({
      supportedUnits,
      getRestrictionReason: (unit) =>
        hasCohortsOverTime
          ? `${capitalize(unit, {strict: true})} queries not available for cohorts and people timeseries.`
          : `${capitalize(unit, {strict: true})} queries not available for behavioral segmentation.`,
    });
  }

  if (isRetentionOnlyInsightsBookmark(bookmarkParams)) {
    const supportedUnits = new Set<TimeUnit>();
    supportedUnits.add(TimeUnit.Day);
    supportedUnits.add(TimeUnit.Week);
    supportedUnits.add(TimeUnit.Month);

    restrictions.push({
      supportedUnits,
      getRestrictionReason: (unit) => `${capitalize(unit, {strict: true})} queries not available for retention.`,
    });
  }

  // Note that the backend allows for 2 project days
  // but calculates days as calendar days and not the logical
  // 48 hour period, so we limit this to 1 day, another reason
  // for this is to reduce the total number of data points to
  // keep the load light on the browser
  const spansMoreThan24Hours = getNumberOfDaysInTimeClause(timeClause) > 1;
  if (spansMoreThan24Hours) {
    const supportedUnits = new Set<TimeUnit>(availableUnits);
    supportedUnits.delete(TimeUnit.Minute);

    restrictions.push({
      supportedUnits,
      getRestrictionReason: (unit) => `${capitalize(unit, {strict: true})} queries can be performed on 24 hour windows`,
    });
  }

  for (const clause of behaviorShowClauses) {
    if (XAU_ONLY_MATH_TYPES.includes(clause.measurement.math as XAU_MATH_TYPES)) {
      const xauLabel = clause.measurement.math.toUpperCase();
      restrictions.push({
        supportedUnits: new Set<TimeUnit>(
          getSupportedTimeUnitsForXauMathType(clause.measurement.math as XAU_MATH_TYPES),
        ),
        getRestrictionReason: (unit) =>
          `${capitalize(unit, {strict: true})} is unavailable when ${xauLabel} is selected`,
      });
    }
  }

  return restrictions;
}

export function getSupportedTimeUnits(bookmarkParams: InsightsBookmarkParams): TimeUnit[] {
  const restrictions = getSupportedTimeUnitRestrictions(bookmarkParams);
  return Object.values(TimeUnit).filter((availableUnit) => {
    return restrictions.every((restriction) => restriction.supportedUnits.has(availableUnit));
  });
}

export function getRestrictionReasonForTimeUnit(bookmarkParams: InsightsBookmarkParams, unit: TimeUnit): null | string {
  const restrictions = getSupportedTimeUnitRestrictions(bookmarkParams);
  for (const restriction of restrictions) {
    if (!restriction.supportedUnits.has(unit)) {
      return restriction.getRestrictionReason(unit);
    }
  }

  return null;
}

export function isFlatSortConfig(config: FlatOrColumnSortConfig): config is FlatSortConfig {
  return Boolean(config) && !(`colSortAttrs` in config);
}

export function _getDefaultInsightsSorting({
  numSegmentColumns,
  liftComparison = false,
  hasPivotTablesEnabled = false,
  canUseViewN,
}: {
  numSegmentColumns: number;
  liftComparison?: boolean;
  hasPivotTablesEnabled?: boolean;
  canUseViewN?: boolean;
}): InsightsSortConfig {
  return {
    [ChartType.Bar]: {
      sortBy: SortBy.Column,
      ...(liftComparison
        ? {
            valueField: `liftComparisonValue`,
          }
        : {}),
      colSortAttrs: [
        {
          sortBy: SortBy.Value,
          sortOrder: SortOrder.Descending,
          ...(liftComparison
            ? {
                valueField: `liftComparisonValue`,
              }
            : {}),
        },
      ],
    },
    line: getDefaultSortConfig({
      numSegmentColumns,
      valueSortField: CommonDataTableFields.Average,
    }),
    table: getDefaultSortConfig({
      numSegmentColumns: hasPivotTablesEnabled ? numSegmentColumns - 1 : numSegmentColumns,
      valueSortField: CommonDataTableFields.Total,
      allowGroupSort: true,
      flatSortConfig: {
        sortBy: SortBy.Value,
        sortOrder: SortOrder.Descending,
        valueField: CommonDataTableFields.Total,
      },
      canUseViewN,
    }),
    [ChartType.Metric]: getDefaultSortConfig({
      numSegmentColumns,
      valueSortField: CommonDataTableFields.Total,
      allowGroupSort: true,
    }),
    [ChartType.Pie]: getDefaultSortConfig({
      numSegmentColumns,
      valueSortField: CommonDataTableFields.Total,
      allowGroupSort: true,
    }),
    [ChartType.RetentionCurve]: getDefaultSortConfig({
      numSegmentColumns: numSegmentColumns || 1,
      valueSortField: `size`,
      flatSortConfig: {
        sortBy: SortBy.Value,
        sortOrder: SortOrder.Descending,
      },
    }),
  };
}

/**
 * Populates the metric column sort config entry for any colSortAttrs config that mismatches
 * the amount of columns to be displayed in a table; this is caused by a previous behavior
 * where we would drop the metric column for single metric, segmented reports
 */
export function fillMetricColumnSort(
  _chartType: ChartType,
  headerCount: number,
  sortConfig: SortConfig,
  valueField: string = CommonDataTableFields.Total,
  _suppressMigrationAlert = true,
) {
  let colSortAttrs = sortConfig?.colSortAttrs;

  if (colSortAttrs && headerCount - colSortAttrs.length === 1) {
    // turn off the sorting config mismatch issue
    // https://mixpanel.slack.com/archives/C0K5RCS3E/p1678827096778989
    // if (suppressMigrationAlert === false) {
    //   reporter.info(`Insights Sort Config Mismatch`, {chartType});
    // }
    // handle the case where data tables would remove the metric column
    // for single metrics that are broken down
    colSortAttrs = [
      {
        valueField,
        sortBy: SortBy.Value,
        sortOrder: SortOrder.Descending,
      },
      ...colSortAttrs,
    ];

    return {...sortConfig, colSortAttrs};
  }

  return sortConfig;
}

// TODO: write some tests for this
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getSortConfig({
  bookmarkSortConfig,
  headerCount = 0,
  numMetrics,
  liftComparison = null,
  hasPivotTablesEnabled = false,
  canUseViewN,
  useDefaultViewNForNewSegments,
}: {
  liftComparison?: LiftComparison;
  headerCount?: number;
  numMetrics?: number;
  bookmarkSortConfig?: Partial<InsightsBookmarkSortConfig>;

  /** @todo - drop once pivot tables is GA */
  hasPivotTablesEnabled?: boolean;
  canUseViewN?: boolean;
  useDefaultViewNForNewSegments?: boolean;
} = {}): InsightsSortConfig {
  const sortConfig: InsightsSortConfig = _getDefaultInsightsSorting({
    numSegmentColumns: headerCount,
    liftComparison: Boolean(liftComparison),
    hasPivotTablesEnabled,
    canUseViewN,
  });

  if (headerCount > 0) {
    if (bookmarkSortConfig) {
      // update existing sorting config for new results

      if (bookmarkSortConfig.bar) {
        // Not using `sanitizeSortConfig()` here because 1) the defaults for new colSortAttrs are different, and
        // 2) unlike other sortConfigs, bar chart sortConfig omits `valueField`
        const colSortAttrs = Array(headerCount);
        for (let i = 0; i < headerCount; i++) {
          // Override the sort by value if we have a lift comparison, since we want that to be the sort rather than underlying value
          if (bookmarkSortConfig.bar.colSortAttrs[i]) {
            colSortAttrs[i] = bookmarkSortConfig.bar.colSortAttrs[i];
            if (liftComparison && colSortAttrs[i].sortBy !== SortBy.Label) {
              colSortAttrs[i].sortBy = SortBy.Value;
            }
          } else {
            colSortAttrs[i] = {
              sortBy: SortBy.Value,
              sortOrder: SortOrder.Descending,
            };
          }
        }
        sortConfig.bar = {
          ...bookmarkSortConfig.bar,
          colSortAttrs,
          ...(liftComparison
            ? {
                valueField: `liftComparisonValue`,
              }
            : {}),
        };

        if (!liftComparison) {
          delete sortConfig.bar.valueField;
        }
      }

      const bookmarkLineSortConfig = bookmarkSortConfig[ChartType.Line];
      if (bookmarkLineSortConfig) {
        if (isFlatSortConfig(bookmarkLineSortConfig)) {
          // silent those for now to avoid rollbar rate limit being exceeded
          // https://mixpanel.slack.com/archives/C0K5RCS3E/p1678827096778989
          // reporter.info(`Insights Deprecated Line SortConfig Parsed`);
          // Convert old bookmarks with flat sort into the multi-seg sort.
          sortConfig.line = getDefaultSortConfig({
            numSegmentColumns: headerCount,
            valueSortField: CommonDataTableFields.Average,
            flatSortConfig: bookmarkLineSortConfig,
          });
        } else {
          sortConfig.line = updateSortingIfSegmentsChange({
            chartType: ChartType.Line,
            sorting: bookmarkLineSortConfig,
            numSegmentColumns: headerCount,
          });
        }
      }

      const bookmarkMetricSortConfig = bookmarkSortConfig[ChartType.Metric];
      if (bookmarkMetricSortConfig) {
        sortConfig[ChartType.Metric] = updateSortingIfSegmentsChange({
          chartType: ChartType.Metric,
          sorting: bookmarkMetricSortConfig,
          numSegmentColumns: headerCount,
        });
      }

      const bookmarkPieSortConfig = bookmarkSortConfig[ChartType.Pie];
      if (bookmarkPieSortConfig) {
        sortConfig[ChartType.Pie] = updateSortingIfSegmentsChange({
          chartType: ChartType.Pie,
          sorting: bookmarkPieSortConfig,
          numSegmentColumns: headerCount,
        });
      }

      const bookmarkTableSortConfig = bookmarkSortConfig[ChartType.Table];
      if (bookmarkTableSortConfig) {
        // Use the default sorting if bookmark has deprecated table sorting
        let validSorting = bookmarkTableSortConfig;
        if (isDeprecatedTableSorting(bookmarkTableSortConfig)) {
          // silent those for now to avoid rollbar rate limit being exceeded
          // https://mixpanel.slack.com/archives/C0K5RCS3E/p1678827096778989
          // reporter.info(`Insights Deprecated Table SortConfig Parsed`);
          validSorting = sortConfig[ChartType.Table];
        }

        if (hasPivotTablesEnabled) {
          if (validSorting.colSortAttrs.length > headerCount) {
            validSorting.colSortAttrs = validSorting.colSortAttrs.slice(1);
          }
          headerCount = headerCount - 1;
        }

        /** before pivot tables, sorting by value was assumed to always be on the `totalValue` field (single metric)
         * pivot tables enables sorting by any column (by value or lexicographical sorting)
         * when switching to multi-seg sort by column, we do not want to inherit the value defined
         * by the flat sorting field, since this can be invalid for multi-seg sorting configs
         * example: when going from flat sort on Browser (A-Z) to multi-seg (Browser and City)
         */
        let sortingValueField = CommonDataTableFields.Total;
        if (
          validSorting.sortBy === SortBy.Value &&
          validSorting.valueField.startsWith(CommonDataTableFields.MetricPrefix)
        ) {
          sortingValueField = validSorting.valueField;
        }

        sortConfig[ChartType.Table] = updateSortingIfSegmentsChange({
          chartType: ChartType.Table,
          sorting: validSorting,
          numSegmentColumns: headerCount,
          numMetricColumns: hasPivotTablesEnabled ? numMetrics : 0,
          canUseViewN,
          useDefaultViewNForNewSegments,
          sortingValueField,
        });
      }
    } else {
      // no existing config; ensure that new config has right number of headers

      // NB: I don't think this branch is hit anymore, at least outside of dashboards: by the time `results`
      // is passed in we already have applied the default sorting from earlier calls to this function
      // this branch is still being hit (a lot) https://mixpanel.slack.com/archives/C0K5RCS3E/p1678827096778989
      // reporter.info(`Insights Sorting Unknown Branch Hit`);

      for (let i = sortConfig.bar.colSortAttrs.length; i < headerCount; i++) {
        sortConfig.bar.colSortAttrs.push({sortBy: SortBy.Value, sortOrder: SortOrder.Descending});
      }

      for (let i = sortConfig.table.colSortAttrs.length; i < headerCount; i++) {
        sortConfig.table.colSortAttrs.push({
          sortBy: SortBy.Value,
          sortOrder: SortOrder.Descending,
          valueField: CommonDataTableFields.Total,
        });
      }
    }
  }

  return sortConfig;
}

function isDeprecatedTableSorting(sorting: InsightsBookmarkSortConfig[ChartType.Table]) {
  // @ts-ignore Property 'sortColumn' does not exist on type 'SortByValueConfig'
  return sorting.sortBy === SortBy.Value && !!sorting.sortColumn;
}

/** Columns in Pie and line Metric data tables that can be group sorted.
 * Key is the field that is group sortable, value is column fields to add (at least sortSelectTitle) */
const GroupSortableFlatInsightsColumns: GroupSortableColumnsDictionary = {
  [CommonDataTableFields.Total]: {sortSelectTitle: `Value`},
};

/** "Sanitizes" sorting (colSortAttrs) and potentially updates to column sort (aka grouped sort) */
export function updateSortingIfSegmentsChange({
  sorting,
  numSegmentColumns,
  numMetricColumns,
  chartType,
  canUseViewN,
  useDefaultViewNForNewSegments,
  sortingValueField,
}: {
  sorting: SortConfig;
  numSegmentColumns: number;
  numMetricColumns?: number;
  chartType: ChartType.Line | ChartType.Metric | ChartType.Pie | ChartType.Table;
  canUseViewN?: boolean;
  useDefaultViewNForNewSegments?: boolean;
  sortingValueField?: string;
}): SortConfig {
  const oldNumSegmentColumns = sorting.colSortAttrs.length;
  const valueField = getPrimaryValueField(sorting);
  const valueFieldIsGroupSortable =
    chartType === ChartType.Table ||
    (chartType === ChartType.Line && GroupSortableTrendFields.includes(valueField)) ||
    ([ChartType.Metric, ChartType.Pie].includes(chartType) && valueField in GroupSortableFlatInsightsColumns);

  return updateSortingForNewNumSegments({
    numSegmentColumns,
    numMetricColumns,
    oldNumSegmentColumns,
    valueFieldIsGroupSortable,
    sorting,
    canUseViewN: canUseViewN && chartType === ChartType.Table,
    useDefaultViewNForNewSegments,
    sortingValueField,
  });
}

export function isPeopleOnlyQuery(bookmarkSections: InsightsBookmarkSections) {
  if (isEmpty(bookmarkSections.show)) {
    return false;
  }

  const clauses = getBehaviorShowClauses(bookmarkSections.show);
  const hasPeopleClauses = clauses.some((clause) => clause.behavior.resourceType === InsightsResourceType.People);
  const hasOnlyFormulasAndPeople = clauses.every(
    (clause) => isFormulaShowClause(clause) || clause.behavior.resourceType === InsightsResourceType.People,
  );

  return hasPeopleClauses && hasOnlyFormulasAndPeople;
}

export function isPeopleTimeSeries(bookmarkSections: InsightsBookmarkSections) {
  const isPeopleOnly = isPeopleOnlyQuery(bookmarkSections);
  const lastGroupClause = bookmarkSections.group && bookmarkSections.group[bookmarkSections.group.length - 1];
  const groupByDatetime = lastGroupClause?.propertyType === PropertyType.Datetime;

  return isPeopleOnly && groupByDatetime;
}

export function getDisplayOptions(
  bookmarkSections: InsightsBookmarkSections,
  bookmarkDisplayOptions: DisplayOptions,
): DisplayOptions {
  const lastGroupClause = bookmarkSections.group && bookmarkSections.group[bookmarkSections.group.length - 1];
  const timeUnit = isPeopleTimeSeries(bookmarkSections) ? lastGroupClause.unit : bookmarkSections.time[0].unit;
  return {
    ...bookmarkDisplayOptions,
    timeUnit,
  };
}

export function getInvalidChartTypesForInsightsBookmark({
  bookmarkParams,
  metricsResource,
}: {
  bookmarkParams: InsightsBookmarkParams;
  metricsResource: MetricsResource;
}): Array<{type: ChartType; reason?: string}> {
  const invalidTypesReasons = new Map<ChartType, string[]>();

  const hasClauses = negate(isEmpty);

  const groupSection = bookmarkParams.sections.group || [];
  const hasCohortsSegments = groupSection.some((clause) => !!clause.cohorts);
  const hasPropSegments = groupSection.some((clause) => !!clause.propertyType);
  if (hasCohortsSegments && !hasPropSegments) {
    addChartTypesInvalidReason([ChartType.StackedBar], `is not available with cohort breakdowns alone`);
  }

  if (hasClauses(bookmarkParams.sections.formula) || getFormulaShowClauses(bookmarkParams.sections.show).length) {
    addChartTypesInvalidReason(
      [ChartType.StackedLine, ChartType.StackedBar, ChartType.Pie],
      `is not available with formula applied`,
    );
  }

  const nonLineChartTypes = ChartResultChartTypes.filter(
    (type) => ![ChartType.Line, ChartType.StackedLine].includes(type),
  );

  const behaviorShowClauses = getBehaviorShowClauses(bookmarkParams.sections.show);

  const hasXauClauses = behaviorShowClauses.some((clause) =>
    (XAU_ONLY_MATH_TYPES as MATH_TYPE[]).includes(clause.measurement.math),
  );

  if (hasXauClauses) {
    addChartTypesInvalidReason(
      nonLineChartTypes,
      `is not available with daily, weekly, or monthly active users\nRemove daily, weekly, or monthly active users to enable`,
    );
  }

  const expandedShowClauses = getExpandedBehaviorShowClauses({
    showClauses: bookmarkParams.sections.show,
    metricsResource,
  });

  if (hasRolling(expandedShowClauses)) {
    addChartTypesInvalidReason(nonLineChartTypes, `is disabled while Rolling is active.\nRemove Rolling to enable`);
  }

  if (hasCumulative(expandedShowClauses)) {
    addChartTypesInvalidReason(
      nonLineChartTypes,
      `is disabled while Cumulative is active.\nRemove Cumulative to enable`,
    );
  }

  // TODO how should this be handled in single report mode with multiple funnels?
  if (!hasAnyOrderFunnel(bookmarkParams.sections.show)) {
    addChartTypesInvalidReason([ChartType.FunnelTopPaths], `is only available with any order funnels`);
  }

  // TODO how should this be handled in single report mode with multiple funnels?
  if (hasTTC(bookmarkParams.sections.show)) {
    addChartTypesInvalidReason([ChartType.FunnelSteps], `is not available with Time to Convert`);
  }

  if (bookmarkParams.timeComparison) {
    addChartTypesInvalidReason(
      [ChartType.Pie, ChartType.StackedLine, ChartType.RetentionCurve],
      `is not available with time comparison`,
    );
  }

  if (bookmarkParams.liftComparison) {
    const liftDescription = `is not available with ${LiftTypeDescription[bookmarkParams.liftComparison.type]}`;
    switch (bookmarkParams.liftComparison.type) {
      case LiftType.Segment:
        addChartTypesInvalidReason([ChartType.Pie, ChartType.StackedBar, ChartType.StackedLine], liftDescription);
        break;
      case LiftType.Overall:
        addChartTypesInvalidReason([ChartType.Pie, ChartType.FunnelSteps], liftDescription);
        break;
      default:
        break;
    }
  }

  const invalidChartTypes = [];

  invalidTypesReasons.forEach((reasons, chartType) => {
    const allReasonsText = reasons.reduce((prev, curr) => `${prev} or ${curr}`);
    invalidChartTypes.push({
      type: chartType,
      reason: `${ChartTypeSelection[chartType].label} ${allReasonsText}.`,
    });
  });

  return invalidChartTypes;

  function addChartTypesInvalidReason(chartTypes: ChartType[], reason: string) {
    chartTypes.forEach((ch) => {
      invalidTypesReasons.has(ch) ? invalidTypesReasons.get(ch).push(reason) : invalidTypesReasons.set(ch, [reason]);
    });
  }
}

function isForecastValue(value: Record<string, unknown>): value is ForecastValue {
  return `yhat_upper` in value && `yhat_lower` in value;
}

/**
 * Flatten a series object tree to show all possible paths + leaf objects of the tree
 * @param {InsightsSeriesTree} series - nested object to be flattened
 * @returns {Array<[string[], Record<string, number>]>} - array of tuples where val1 is a path array and val2 is the corresponding leaf object
 * @example
 * flattenSeries({
 *    "Viewed Report": {
 *      "United States": {
 *        "2020-10-01T00:00:00-07:00": 15,
 *        "2020-11-01T00:00:00-07:00": 1,
 *      },
 *      "Australia": {
 *        "2020-10-01T00:00:00-07:00": 3,
 *        "2020-11-01T00:00:00-07:00": 4,
 *      },
 *    },
 * });
 * // [
 * //   [ ["Viewed Report", "United States"], {"2020-10-01T00:00:00-07:00": 15, "2020-11-01T00:00:00-07:00": 1} ],
 * //   [ ["Viewed Report", "Australia"], {"2020-10-01T00:00:00-07:00": 3, "2020-11-01T00:00:00-07:00": 4} ],
 * // ]
 */
export function flattenSeries<ValueT>(series: InsightsSeriesTree<ValueT>): Array<[string[], Record<string, ValueT>]> {
  const flattenedEntries: Array<[string[], Record<string, ValueT>]> = [];

  // basic breadth-first tree traversal (non-recursive)
  const breadthQueue: Array<[string[], InsightsSeriesTree<ValueT>]> = [[[], series]];
  while (breadthQueue.length > 0) {
    const [path, obj] = breadthQueue.shift();
    for (const [key, value] of Object.entries(obj)) {
      const isSeriesValueType = !isObject(value) || isForecastValue(value as Record<string, unknown>);
      if (isSeriesValueType) {
        flattenedEntries.push([path, obj as Record<string, ValueT>]);
        break;
      } else {
        breadthQueue.push([[...path, key], value as InsightsSeriesTree<ValueT>]);
      }
    }
  }

  return flattenedEntries;
}

/*
 * Depth first implementation of flattenSeries which lets you go to a certain depth
 */
export function flattenSeriesToDepth<ValueT>(
  series: InsightsSeriesTree<ValueT>,
  maxDepth = Infinity,
): Array<[string[], InsightsSeriesTree<ValueT>]> {
  const flattenedEntries: Array<[string[], InsightsSeriesTree<ValueT>]> = [];

  // basic depth-first tree traversal (recursive)
  function depthFirstTraversal(path: string[], obj: InsightsSeriesTree<ValueT>, depth: number): void {
    // segments should only contain valid segment values. filter $overall, $other
    delete obj[`$overall`];
    delete obj[`$other`];

    if (depth > maxDepth) {
      flattenedEntries.push([path, obj as Record<string, ValueT>]);
      return;
    }

    for (const [key, value] of Object.entries(obj)) {
      const isSeriesValueType = !isObject(value) || isForecastValue(value as Record<string, unknown>);
      if (isSeriesValueType) {
        flattenedEntries.push([path, obj as Record<string, ValueT>]);
      } else {
        depthFirstTraversal([...path, key], value as InsightsSeriesTree<ValueT>, depth + 1);
      }
    }
  }

  depthFirstTraversal([], series, 0);

  return flattenedEntries;
}

/**
 * Checks if the results series has a single root node with at least one breakdown.
 * Useful to check because for single root breakdowns, the root node is typically ignored when displaying segment names.
 * @param {InsightsQueryResults} results - raw results from an Insights query
 * @returns {boolean}
 */
export function isSingleSegmentedMetric<ValueT>(
  resultHeaders: string[],
  resultSeries: InsightsSeriesTree<ValueT>,
): boolean {
  return resultHeaders.length > 1 && Object.keys(resultSeries).length === 1;
}

/**
 * Converts the Insights query results series into an flat array of data segments
 * @param {string[]} resultHeaders
 * @param {InsightsSeriesTree[]} resultSeries
 * @param {GroupClause[]} groupClauses - list of group clauses needed to construct the list of indices to omit
 * @returns {FlatSegment[]}
 * Note: that $overall sub-series/nodes are not removed. Up to the client of this method to filter/use them as needed.
 */
export function getFlatSegments<ValueT extends number | Record<string, number>>(
  headers: string[],
  resultSeries: InsightsSeriesTree<ValueT>,
): Array<FlatSegment<ValueT>> {
  const series: InsightsSeriesTree<ValueT> = resultSeries;

  const flattenedTuples = flattenSeries<ValueT>(series);
  return flattenedTuples.map(([path, value]: [string[], Record<string, ValueT>]) => {
    // DisplayName should only show the values of the implicit segments, otherwise display a placeholder
    const displayNamePath = path.map((segment, index) => {
      return renamePropertyValue(segment, headers[index]);
    });

    return {
      path,
      displayNamePath,
      key: path.join(),
      value: mapValues(
        value,
        (v, _): ValueT => {
          if (isObject(v)) {
            return mapValues((v as any) as Record<string, number>, roundInsightsValue) as ValueT;
          }
          return roundInsightsValue(v) as ValueT;
        },
      ),
      displayName: displayNamePath.join(` / `),
    };
  });
}

export function _getPivotTableSegmentMetadata({
  series,
  parentSeries,
  liftType,
  comparisonSeries,
  liftSeries,
  metrics,
  label,
}: {
  series: InsightsSeriesTree;
  parentSeries: InsightsSeriesTree;
  liftType?: LiftType;
  comparisonSeries?: InsightsSeriesTree;
  liftSeries?: InsightsSeriesTree;
  metrics: Record<string, string>;
  label: string;
}): SegmentMetadataNode {
  function getComparisonValue(comparisonNode, metricId) {
    let comparisonOverall;
    if (comparisonNode) {
      // use comparisonSeries if provided such as for time comparisons
      comparisonOverall = comparisonNode[metricId];
    } else if (liftType === LiftType.Overall) {
      // for percentOverall lift, we get the percentage of the total of the parent node
      // the $root node has no parent nodes and is the percentage of itself
      comparisonOverall =
        label === `$root` ? series[RESULTS_OVERALL_SUM_KEY][metricId] : parentSeries[RESULTS_OVERALL_SUM_KEY][metricId];
    }
    return comparisonOverall;
  }

  if (typeof Object.values(series)[0] === `number`) {
    // leaf node
    type LeafNode = {[k: string]: number};
    return {
      type: TreeNodeType.Leaf,
      label,
      metricMetadatas: Object.keys(metrics).map((metricId) => {
        return {
          metricOverall: (series as LeafNode)[metricId],
          liftOverall: liftSeries && (liftSeries as LeafNode)[metricId],
          comparisonOverall: getComparisonValue(comparisonSeries, metricId),
        };
      }),
    };
  } else {
    type ParentNode = {[k: string]: InsightsSeriesTree};
    return {
      type: TreeNodeType.Parent,
      label,
      children: Object.keys(series)
        .filter((label) => label !== RESULTS_OVERALL_SUM_KEY)
        .map((label) => {
          return _getPivotTableSegmentMetadata({
            series: (series as ParentNode)[label],
            parentSeries: series,
            liftType,
            comparisonSeries: comparisonSeries && (comparisonSeries as ParentNode)[label],
            liftSeries: liftSeries && (liftSeries as ParentNode)[label],
            metrics,
            label,
          });
        }),
      metricMetadatas: Object.keys(metrics).map((metricId) => {
        return {
          metricOverall: series[RESULTS_OVERALL_SUM_KEY]?.[metricId],
          liftOverall: liftSeries && liftSeries[RESULTS_OVERALL_SUM_KEY][metricId],
          comparisonOverall: getComparisonValue(
            comparisonSeries && comparisonSeries[RESULTS_OVERALL_SUM_KEY],
            metricId,
          ),
        };
      }),
    };
  }
}

export function getPivotTableSegmentMetadata({
  series,
  liftType,
  comparisonSeries,
  liftSeries,
  metrics,
}: {
  series: InsightsSeriesTree;
  liftType?: LiftType;
  comparisonSeries?: InsightsSeriesTree;
  liftSeries?: InsightsSeriesTree;
  metrics: Record<string, string>;
}): SegmentMetadataNode {
  return _getPivotTableSegmentMetadata({
    series,
    parentSeries: null,
    liftType,
    liftSeries,
    comparisonSeries,
    metrics,
    label: `$root`,
  });
}

export function getSegmentLookupMap<ValueT>(segments: Array<FlatSegment<ValueT>>): Map<string, FlatSegment<ValueT>> {
  return new Map(segments.map((segment) => [segment.key, segment]));
}

/**
 * Return a timeseries object (where the key is an ISO date string and the value is the metric value for that date)
 * that uses the dates from @mainTimeseries with the values from @comparisonTimeseries.
 * An empty @comparisonTimeseries object will produce a zero-value timeseries.
 * @param {Record<string, number>} mainTimeseries - the timeseries with current dates for the segment
 * @param {Record<string, number>} comparisonTimeseries - a different timeseries, must be either empty or the same key-length as @mainTimeseries
 * @param {boolean} shouldTurnToPercentage - use data values (.XXXX) values to percentages (XX.XX)
 */
export function getTimeseriesFromTimeComparisonValues(
  mainTimeseries: Record<string, number>,
  comparisonTimeseries: Record<string, number>,
  shouldTurnToPercentage?: boolean,
): Record<string, number> {
  const dates = sortIsoDates(Object.keys(mainTimeseries));
  const comparisonDates = Object.keys(comparisonTimeseries);

  const values = sortIsoDates(comparisonDates).map((key) => comparisonTimeseries[key]);
  return dates.reduce<Record<string, number>>((timeseries, date, idx): Record<string, number> => {
    timeseries[date] = values[idx] ?? 0;
    if (shouldTurnToPercentage) {
      timeseries[date] *= 100; //Multiply by 100 to turn into a percentage
    }
    return timeseries;
  }, {});
}

// converting a date string to a numeric timestamp for Highcharts is a very frequently executed task but very slow with moment.js,
// memoizing the process produces huge performance gains for large datasets
export const memoizedGetTimestamp = memoize(
  (isoDate: string, timezone: string, utcOffset: number) => {
    const date = moment(isoDate, moment.ISO_8601, true);
    return offsetForHighcharts(date, timezone, utcOffset).valueOf();
  },
  (isoDate: string, timezone: string, utcOffset: number) => {
    return [isoDate, timezone, utcOffset].join(`|`);
  },
);

export function getSortedIsoDateKeysFromTimeseries(timeseries: Record<string, unknown>): string[] {
  const isoDates = Object.keys(timeseries).filter((dateKey) => {
    // Note: sometimes query-API adds non-date entries into the timeseries.
    // eg. people-only breakdown-by-date line-chart-type queries will return timeseries with an "$overall" entry next to regular date entries
    return dateKey !== RESULTS_OVERALL_SUM_KEY;
  });
  return sortIsoDates(isoDates);
}

/**
 * Return an array of timestamp tuples from a timeseries object
 * @param {Record<string, number>} timeseries - JSON object where the key is an ISO date string and the value is the metric value for that date
 * @param {ProjectTimezone} projectTimezone - details about the project timeseries
 * @returns {Array<[number, number]>} - array of tuples where val1 is a Unix timestamp in milliseconds and val2 is the metric value for that timestamp
 */
export function getTimestampTuplesFromTimeseries<ValueT>(
  timeseries: Record<string, ValueT>,
  projectTimezone: ProjectTimezone,
): Array<[number, ValueT]> {
  return getSortedIsoDateKeysFromTimeseries(timeseries).map((dateKey: string) => {
    // convert ISO-formatted date string into a Unix timestamp in milliseconds
    const timestamp = memoizedGetTimestamp(dateKey, projectTimezone.timezone, projectTimezone.utcOffset);
    const value = timeseries[dateKey];

    return [timestamp, value];
  });
}

function getFirstTimeseries(series: InsightsSeriesTree): Record<string, number> {
  let node: any = series;
  while (isObject(find(node))) {
    node = find(node);
  }
  return node as Record<string, number>;
}

/**
 * Return dates for each bucket Insights results timeseries. Dates are inferred if possible.
 * @param results - Insights results from query-api
 * @returns - arrays of ISO 8601 date strings for main & time comparison series
 */
export function getTimeseriesDates(
  results: InsightsQueryResults,
  typeOfComparison: TypeOfComparison,
): {main: string[]; timeComparison: string[]} {
  const timeseries = getFirstTimeseries(results.series);
  let dates = Object.keys(timeseries);

  const comparisonTimeseries = getFirstTimeseries(results[typeOfComparison]?.series ?? {});
  let comparisonDates = Object.keys(comparisonTimeseries);

  if (
    typeOfComparison === TypeOfComparison.Time &&
    (dates.length === 0 || comparisonDates.length === 0) &&
    results.time_comparison?.date_range
  ) {
    // if either main or time comparison series are empty, we have to infer dates based on the offset
    const offsetMs = getTimeComparisonOffsetMs({
      dateRange: {
        fromDate: results.date_range.from_date,
        toDate: results.date_range.to_date,
      },
      timeComparisonDateRange: {
        fromDate: results.time_comparison.date_range.from_date,
        toDate: results.time_comparison.date_range.to_date,
      },
    });
    const dateFormat = moment.parseZone(results.date_range.from_date, moment.ISO_8601).creationData().format.toString();

    if (dates.length === 0 && comparisonDates.length > 0) {
      dates = comparisonDates.map((date) => {
        return moment.parseZone(date, moment.ISO_8601).add(offsetMs, `ms`).format(dateFormat);
      });
    }

    if (dates.length > 0 && comparisonDates.length === 0) {
      comparisonDates = dates.map((date) => {
        return moment.parseZone(date, moment.ISO_8601).subtract(offsetMs, `ms`).format(dateFormat);
      });
    }
  }

  return {main: dates, timeComparison: comparisonDates};
}

/**
 * Return an array of zero-value flat segments that are missing from @reference (missing-check is based on segment key).
 * @param {FlatSegment[]} reference - array of flat segments to check against
 * @param {FlatSegment[]} subset - array of flat segments that may be missing segments compared to @reference
 * @param {FlatSegment[]} timeseriesDates - array of ISO 8601 date strings to use as object keys for the output segment values
 * @returns {FlatSegment[]} - array of flat segments with zero-value segments; the segment value will be an empty object if @subset is an empty array
 */
export function getMissingZeroSegments(
  reference: FlatSegment[],
  subset: FlatSegment[],
  timeseriesDates: string[],
): FlatSegment[] {
  const missingSegments = differenceBy(reference, subset, `key`);
  const zeroTimeseries = timeseriesDates.reduce<Record<string, number>>((timeseries, date): Record<string, number> => {
    timeseries[date] = 0;
    return timeseries;
  }, {});
  return missingSegments.map((segment) => {
    return {
      ...segment,
      value: zeroTimeseries,
    };
  });
}

export function getNestedHeaderTypes(obj: InsightsSeriesTree): HeaderType[] {
  function _getNestedHeaderTypes(obj, types, maxDepth, currDepth) {
    if (currDepth === 0) {
      return;
    }

    for (const [key, value] of Object.entries(obj)) {
      if (key === RESULTS_OVERALL_SUM_KEY) {
        // Ignore $overall nodes because they don't contain all segments
        // (last segment was always being marked as a date type)
        continue;
      }
      const index = maxDepth - currDepth;
      if (!Object.prototype.hasOwnProperty.call(types, index)) {
        const headerType = getHeaderType(key);
        if (headerType) {
          types[index] = headerType;
        }
      }
      _getNestedHeaderTypes(value, types, maxDepth, currDepth - 1);
      if (Object.keys(types).length === maxDepth) {
        // Early termination of depth-first traversal if types at all depths have been determined
        return;
      }
    }
  }

  const types = [];
  const depth = nestedObjectDepthInsightsFormat(obj);
  _getNestedHeaderTypes(obj, types, depth, depth);

  // if a header type at any depth was unable to be determined, default to string
  for (let currDepth = 0; currDepth < depth; currDepth++) {
    types[currDepth] = types[currDepth] ?? HeaderType.STRING;
  }

  return types;
}

/**
 * Extract a single number from flat segments. If value is a timeseries, sums the timeseries.
 */
export function getSingleNumberFromSegmentValue(segmentValue: Record<string, number>): number {
  const values = Object.values(segmentValue);
  return sum(values);
}

/**
 * Convert an InsightsSeriesTree such that each parent has an nested children object keyed by label
 * and a value which is the unsegmented value.
 *
 * Adds an "unsegmented" property which reflects the api-supplied $overall, and a "sumOfChildren" property, which is the sum of the child segments present.
 */
export function createUnorderedSumTree(
  series: InsightsSeriesTree,
  timeComparisonSeries: InsightsSeriesTree,
  liftComparisonSeries: InsightsSeriesTree,
  baselineKey: string,
  headerTypes: string[],
  currPath: string[] = [],
): UnorderedSumTree {
  const headerType = headerTypes[0];

  const keys = Array.from(new Set([...Object.keys(series), ...Object.keys(timeComparisonSeries ?? {})]));

  return keys.reduce((acc, key) => {
    const path = [...currPath, key];

    // if key is not present in the main series but is in the time comparison series,
    // pad the main series with zero values from the time comparison series.
    if (!Object.prototype.hasOwnProperty.call(series, key)) {
      series[key] = deepMapValuesToZero(timeComparisonSeries[key]);
    }

    const leafValue = getValueIfLeaf(series[key] as InsightsSeriesTree);
    if (leafValue !== null) {
      acc[key] = {
        label: key,
        value: leafValue,
        timeComparisonValue: getValueIfLeaf((timeComparisonSeries?.[key] ?? {}) as InsightsSeriesTree) ?? 0,
        liftComparisonValue: getValueIfLeaf((liftComparisonSeries?.[key] ?? {}) as InsightsSeriesTree) ?? 0,
        headerType,
        path,
        ...(baselineKey ? {baselineComparisonUnit: baselineKey} : {}),
        ...(baselineKey && keys.includes(baselineKey)
          ? {
              baselineComparisonUnit: baselineKey,
              baselineComparisonValue: getValueIfLeaf((series[baselineKey] ?? {}) as InsightsSeriesTree) ?? 0,
            }
          : {}),
      };
    } else {
      const children = createUnorderedSumTree(
        series[key] as InsightsSeriesTree,
        timeComparisonSeries?.[key] as InsightsSeriesTree,
        liftComparisonSeries?.[key] as InsightsSeriesTree,
        baselineKey,
        headerTypes.slice(1),
        path,
      );

      // with multiple events, when some are distribution and others are not,
      // some elements at this level will NOT have $overall. It is as if it is not segmented, so All is appropriate
      const summaryNode = children[RESULTS_OVERALL_SUM_KEY] || children[`All`];

      let value = 0,
        timeComparisonValue = 0,
        liftComparisonValue = 0;

      let unsegmented = null,
        sumOfChildren = null;

      const childNodes = Object.values(children).filter((node) => !node.path.includes(RESULTS_OVERALL_SUM_KEY));
      sumOfChildren = {
        value: sumBy(childNodes, (childNode) => (childNode.sumOfChildren || childNode).value),
        timeComparisonValue: sumBy(
          childNodes,
          (childNode) => (childNode.sumOfChildren || childNode).timeComparisonValue,
        ),
        liftComparisonValue: sumBy(
          childNodes,
          (childNode) => (childNode.sumOfChildren || childNode).liftComparisonValue,
        ),
      };

      if (summaryNode) {
        value = summaryNode.value;
        timeComparisonValue = summaryNode.timeComparisonValue;
        liftComparisonValue = summaryNode.liftComparisonValue;
        unsegmented = {
          value: summaryNode.value,
          timeComparisonValue: summaryNode.timeComparisonValue,
          liftComparisonValue: summaryNode.liftComparisonValue,
        };
      } else {
        // TODO: confirm we should never lack an $overall node when there are child nodes
        console.warn(`No $overall node in insights result`, {path, key, childCount: childNodes.length});
        unsegmented = sumOfChildren;
      }

      acc[key] = {
        label: key,
        value,
        timeComparisonValue,
        liftComparisonValue,
        children,
        headerType,
        path,
        ...(unsegmented ? {unsegmented} : {}),
        ...(sumOfChildren ? {sumOfChildren} : {}),
      };

      delete children[RESULTS_OVERALL_SUM_KEY];
    }
    return acc;
  }, {});
}

/**
 * Given a sum tree, merge all nodes at each depth by label and add their corresponding values together
 * @returns {AggregatedSeries[]} - an array indexed by depth where the values are
 * objects with the node label as key, and an object with total and list of paths as value
 */
export function getAggregatedSeriesList(tree: UnorderedSumTree): AggregatedSeries[] {
  function helper(nodes: UnorderedSumTreeNode[], aggregatedSeries) {
    const aggregated = nodes.reduce((acc, node) => {
      if (getOwnAttr(acc, node.label)) {
        acc[node.label].total += node.value;
        acc[node.label].paths.push(node.path);
      } else {
        acc[node.label] = {
          total: node.value,
          paths: [node.path],
        };
      }

      return acc;
    }, {});

    const combined = [...aggregatedSeries, aggregated];

    if (nodes.some((node) => !!node.children)) {
      const children = nodes
        .filter((node) => !node.path.includes(RESULTS_OVERALL_SUM_KEY) && !node.path.includes(RESULTS_OTHER_SUM_KEY))
        .flatMap((node) => {
          return Object.values(node.children).filter((node) => !node.path.includes(RESULTS_OVERALL_SUM_KEY));
        });
      return helper(children, combined);
    } else {
      return combined;
    }
  }

  return helper(Object.values(tree), []);
}

/**
 * Sort an aggregated series by sortOption.
 * Attempts to get the header type of the series for more accurate sorting.
 */
export function getSortedAggregatedSeriesKeys(
  aggregatedSeries: AggregatedSeries,
  sortOption: FlatSortConfig,
): string[] {
  const seriesKeys = Object.keys(aggregatedSeries);

  let sortType;
  if (sortOption.sortBy === SortBy.Value) {
    sortType = HeaderType.NUMBER;
  } else {
    for (const label of seriesKeys) {
      if (label !== `undefined`) {
        sortType = getHeaderType(label);
        break;
      }
    }

    sortType = sortType || HeaderType.STRING;
  }

  return seriesKeys.sort(
    typedComparator({
      sortType,
      order: sortOption.sortOrder,
      transform: (item) => {
        return sortOption.sortBy === SortBy.Value ? aggregatedSeries[item].total.toString() : item;
      },
    }),
  );
}

/**
 * Given an aggregated series, returns a corresponding object where the value is the index at which it would apppear
 * when sorting by the value of that node.
 */
export function getSortedKeyMap(aggregatedSeries: AggregatedSeries): {[label: string]: number} {
  const sortedKeys = sorted(Object.keys(aggregatedSeries), {
    order: OrderBy.Descending,
    transform: (key) => Math.abs(aggregatedSeries[key].total),
  });

  return sortedKeys.reduce((keyMap, key, idx) => {
    keyMap[key] = idx;
    return keyMap;
  }, {});
}

export function getSortedKeyMapForFlatSegments(segments: FlatSegment[]): {[label: string]: number} {
  const aggregatedSeries: AggregatedSeries = segments.reduce((aggregate: AggregatedSeries, segment) => {
    aggregate[segment.key] = {
      paths: [segment.path],
      total: getSingleNumberFromSegmentValue(segment.value),
    };
    return aggregate;
  }, {});

  return getSortedKeyMap(aggregatedSeries);
}

export const INSIGHTS_RESOURCE_TYPE_TO_COMMON_RESOURCE_TYPE = {
  [InsightsResourceType.Events]: ResourceType.Event,
  [InsightsResourceType.Formulas]: ResourceType.Formula,
  [InsightsResourceType.Cohorts]: ResourceType.Cohort,
  [InsightsResourceType.People]: ResourceType.User,
};

/**
 * Insights rely on `group` for many things (e.g. getting group header). Transitioning to Single Segment requires that
 * we use `group_by` when single segment feature flag is present.
 * @param sections
 */
function getInsightsGroupBy(sections: InsightsBookmarkSections) {
  let groupByArr = [];
  if (Object.prototype.hasOwnProperty.call(sections, `group_by`)) {
    groupByArr = sections.group_by;
  } else {
    groupByArr = sections.group;
  }
  return groupByArr;
}

/**
 * Derive series header display names using bookmark params as query-api does not always return lexicon rename-able
 * headers in results (i.e. for nested object props, data groups).
 *
 * @param sections
 * @param resultHeaders
 * @param dataGroups
 * @param options.includeMetricHeader for a pivot table, the metric column is not considered part
 * of the segmentation path so it's omitted from a data table's segment columns
 *
 * @returns string[]
 */
/* eslint-disable sonarjs/cognitive-complexity */
export function getSeriesDisplayHeaders(
  sections: InsightsBookmarkSections,
  resultHeaders: string[],
  dataGroups: Record<string, DataGroup>,
  options: {includeMetricHeader?: boolean} = {},
): string[] {
  // All show clauses for series data share the same header / resource type
  let resourceType: InsightsResourceType;
  let dataGroupId: string;
  const firstShowClause = sections.show[0];
  if (isFormulaShowClause(firstShowClause)) {
    resourceType = InsightsResourceType.Formulas;
  } else {
    ({resourceType, dataGroupId} = firstShowClause.behavior);
  }
  const {includeMetricHeader = true} = options;

  let metricHeader;

  // Use data group display name instead of $people header
  if (resourceType === InsightsResourceType.People) {
    metricHeader = dataGroupId && dataGroupId in dataGroups ? dataGroups[dataGroupId].displayName : `Users`;
  } else {
    metricHeader = propertiesUtils.renameProperty(
      resultHeaders[0],
      INSIGHTS_RESOURCE_TYPE_TO_COMMON_RESOURCE_TYPE[resourceType],
      {
        dataGroupId,
      },
    );
  }

  // For funnels in insights report mode
  const isFunnel =
    sections.show.length === 1 &&
    sections.show.every(isBehaviorShowClause) &&
    sections.show[0].behavior.type === MetricType.Funnel;
  const eventComparisonHeaders = [];
  if (isFunnel) {
    metricHeader = `Funnel`;
    const behaviors = (sections.show[0] as BehaviorShowClause).behavior.behaviors || [];
    behaviors.forEach((beh, idx) => {
      if (beh.behaviors && beh.behaviors.length >= 2) {
        eventComparisonHeaders.push(`Step ` + (idx + 1) + ` Comparison`);
      }
    });
  }
  const isRetention =
    sections.show.length === 1 &&
    sections.show.every(isBehaviorShowClause) &&
    [MetricType.Retention, MetricType.RetentionFrequency].includes(sections.show[0].behavior.type);
  if (isRetention) {
    metricHeader = `Retention`;
  }

  // Cohort clauses are always grouped into the same header and always come first after the `show` header
  const hasCohorts = sections.cohorts?.length;

  // Distribution header will always be last, only need one show clause with distribution
  const hasDistributionAggregation = getBehaviorShowClauses(sections.show).some(
    (c) => c.measurement.math === PRIMITIVE_MATH_TYPES.HISTOGRAM,
  );

  const groupHeaders = getInsightsGroupBy(sections).map((clause, index) => {
    const {cohorts, dataGroupId, resourceType, value, propertyObjectKey, customProperty, listItemGroup} = clause;
    if (cohorts?.length) {
      return `Cohort`;
    }
    const commonResourceType = INSIGHTS_RESOURCE_TYPE_TO_COMMON_RESOURCE_TYPE[resourceType];
    const queryIndex = index + (includeMetricHeader ? 1 : 0) + (hasCohorts ? 1 : 0);

    // TODO can we stay in sync with the query as much as possible?
    return isEventBasedBehavioralSegmentation(clause)
      ? resultHeaders[queryIndex]
      : propertiesUtils.renameProperty(value, commonResourceType, {
          dataGroupId,
          customProperty,
          propertyObjectKey,
          listItemGroup,
        });
  });

  const hasFunnelStepSpecialHeaders =
    isFunnel &&
    resultHeaders.find((header) => header === `Metric`) &&
    resultHeaders.find((header) => header === `Step`);

  const derivedHeaders = [
    ...(includeMetricHeader ? [metricHeader] : []),
    ...eventComparisonHeaders,
    ...(hasCohorts ? [`Cohort`] : []),
    ...groupHeaders,
    ...(hasDistributionAggregation ? [`Distribution`] : []),
    ...(hasFunnelStepSpecialHeaders ? [`Metric`, `Step`] : []),
  ];

  if (derivedHeaders.length !== resultHeaders.length) {
    reporter.error(`Unable to accurately derive insights series headers`, {derivedHeaders, resultHeaders});
    return resultHeaders;
  }

  return derivedHeaders;
}
/* eslint-enable sonarjs/cognitive-complexity */

/**
 *
 * @param sections
 * @param results
 * @param dataGroups
 * @param options.includeMetricHeader for a pivot table, the metric column is not considered part
 * of the segmentation path so it's omitted from a data table's segment columns
 *
 * @returns SegmentColumnSpec[]
 */
export function getSeriesDisplaySegmentColumns(
  sections: InsightsBookmarkSections,
  results: InsightsQueryResults,
  dataGroups: Record<string, DataGroup>,
  options: {includeMetricHeader?: boolean} = {},
): SegmentColumnSpec[] {
  const {includeMetricHeader = true} = options;

  const headers = getSeriesDisplayHeaders(sections, results.headers, dataGroups, {includeMetricHeader});
  const headerTypes = getNestedHeaderTypes(results.series);
  if (headers.length !== headerTypes.length) {
    reporter.error(`getSeriesDisplaySegmentColumns: length mismatch between headers and headerTypes`, {
      headers,
      headerTypes,
    });
    return headers.map((header) => ({
      title: header,
      segmentType: PropertyType.String,
      propertyName: header,
    }));
  }
  return zip(headers, headerTypes).map(([header, headerType]) => {
    return {
      title: header,
      propertyName: header,
      segmentType: getPropertyTypeFromHeaderType(headerType),
    };
  });
}

/**
 * When there's no time comparison configured by the user,
 * we can infer a default time comparison by shifting the date range window.
 */
// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getDefaultTimeComparisonOffset(
  bookmarkTimeSection: TimeClause,
  utcOffset: number,
  dateRangeOverride?: DateRange,
): RelativeDate {
  let fromDate = null,
    toDate = null;

  if (dateRangeOverride) {
    // use date range override
    if (dateRangeOverride.type === DateRangeType.RelativeAfter) {
      return dateRangeOverride.window;
    } else {
      fromDate = dateRangeOverride.from;
      toDate = dateRangeOverride.to;
    }
  } else {
    // use bookmark time section
    const {unit, value, window} = bookmarkTimeSection;
    if (window) {
      return window;
    } else if (isNumber(value) && unit) {
      return {value, unit};
    } else if (isArray(value) && value.length > 0) {
      [fromDate, toDate] = value;
    } else if (isString(value)) {
      // could be "$start_of_current_day"
      fromDate = value;
    }
  }

  // use days between fromDate and toDate
  const fromCalendarDate =
    fromDate &&
    (DateRange.isRelativeDate(fromDate)
      ? DateRange.convertRelativeDateToCalendarDate(fromDate)
      : DateRange.convertAbsoluteDateToCalendarDate(fromDate));
  const toCalendarDate =
    toDate &&
    (DateRange.isRelativeDate(toDate)
      ? DateRange.convertRelativeDateToCalendarDate(toDate)
      : DateRange.convertAbsoluteDateToCalendarDate(toDate));

  if (fromCalendarDate && !toCalendarDate) {
    // offset now to be project time (shift local and project offsets)
    const now = moment().add(utcOffset - moment().utcOffset(), TimeUnit.Minute);

    return {
      unit: TimeUnit.Day,
      value: now.diff(fromCalendarDate, `days`) + 1,
    };
  } else if (fromCalendarDate && toCalendarDate) {
    return {
      unit: TimeUnit.Day,
      value: moment(toCalendarDate).diff(fromCalendarDate, `days`) + 1,
    };
  }

  throw new Error(`invalid date range override or bookmark time section; unable to get time comparison info`);
}

// TODO: support line charts
export function getFlatSegmentTableRow({
  segment,
  timeComparisonSegment,
}: {
  segment: FlatSegment;
  timeComparisonSegment?: FlatSegment;
}): TableRow {
  const headerStartIdx = 0;

  const path = segment.path.slice(headerStartIdx);
  const segmentNames = new Array(path.length)
    .fill(null)
    .map((_, idx) => `${CommonDataTableFields.SegmentPrefix}${idx}`);

  const segmentValueMap = Object.fromEntries(zip(segmentNames, path));

  return {
    identifier: segment.key,
    ...segmentValueMap,
    [CommonDataTableFields.Total]: getSingleNumberFromSegmentValue(segment.value),
    ...(timeComparisonSegment
      ? {[CommonDataTableFields.TimeComparisonTotal]: getSingleNumberFromSegmentValue(timeComparisonSegment.value)}
      : {}),
  };
}

export function getFlatSegmentDataTableColumns({
  segmentColumns,
  hasTimeComparison,
  cellRenderer = null,
  sortDisabled = false,
}: {
  segmentColumns: SegmentColumnSpec[];
  hasTimeComparison: boolean;
  cellRenderer?: CellRendererFunc;
  sortDisabled?: boolean;
}): TableColumn[] {
  const columns: TableColumn[] = segmentColumns.map(({title, segmentType, propertyName}, idx) => ({
    field: `${CommonDataTableFields.SegmentPrefix}${idx}`,
    type: TableColumnType.Segment,
    contentType: segmentType,
    title,
    sticky: true,
    isSortable: !sortDisabled,
    labelTransform: (row, field) => renamePropertyValue(row[field], propertyName),
  }));

  cellRenderer ||= ({item, context}) => formatDataTableNumber(item[context.field] ?? 0, {isAverage: false});

  columns.push({
    field: CommonDataTableFields.Total,
    type: TableColumnType.Data,
    contentType: TableDataType.Number,
    title: `Value`,
    cellRenderer,
    isSortable: !sortDisabled,
    sticky: false,
    resizable: hasTimeComparison,
    ...GroupSortableFlatInsightsColumns[CommonDataTableFields.Total],
  });

  if (hasTimeComparison) {
    columns.push({
      field: CommonDataTableFields.TimeComparisonTotal,
      type: TableColumnType.Data,
      contentType: TableDataType.Number,
      title: `Value (Past)`,
      cellRenderer,
      isSortable: !sortDisabled,
      sticky: false,
      resizable: false,
    });
  }

  return columns;
}

export function getTrendDataRow({
  isSelected,
  segmentItem,
  segment,
  timeComparisonSegment,
  liftComparisonSegment,
  projectTimezone,
}: {
  isSelected: boolean;
  segmentItem: SegmentItem;
  segment: FlatSegment;
  timeComparisonSegment?: FlatSegment;
  liftComparisonSegment?: FlatSegment;
  projectTimezone: ProjectTimezone;
}): TrendDataRow {
  function mapIsoDateToInfo(isoDate) {
    return {
      isoDate,
      timestamp: memoizedGetTimestamp(isoDate, projectTimezone.timezone, projectTimezone.utcOffset),
    };
  }

  const sortedDateInfos = sortIsoDates(Object.keys(segment.value)).map(mapIsoDateToInfo);

  let timeComparisonData = null;
  let liftComparisonData = null;

  if (liftComparisonSegment) {
    const liftComparisonSortedDateInfos = sortIsoDates(Object.keys(liftComparisonSegment.value)).map(mapIsoDateToInfo);
    liftComparisonData = liftComparisonSortedDateInfos.map((dateInfo) => ({
      timestamp: dateInfo.timestamp,
      value: liftComparisonSegment.value[dateInfo.isoDate],
    }));
  } else if (timeComparisonSegment) {
    const timeComparisonSortedDateInfos = sortIsoDates(Object.keys(timeComparisonSegment.value)).map(mapIsoDateToInfo);
    timeComparisonData = timeComparisonSortedDateInfos.map((dateInfo) => {
      return {
        timestamp: dateInfo.timestamp,
        value: timeComparisonSegment.value[dateInfo.isoDate],
      };
    });
  }

  let segmentValues = segment.path;
  // segmentValues must honor displayName
  segmentValues = segmentValues.map((path) => (path === segment.key ? segment.displayName : path));

  return {
    rowId: segmentItem.id,
    selectionColor: segmentItem.color,
    isSelected,
    segmentValues,
    data: sortedDateInfos.map((dateInfo) => {
      return {
        timestamp: dateInfo.timestamp,
        value: segment.value[dateInfo.isoDate],
      };
    }),
    ...(timeComparisonData ? {timeComparisonData} : {}),
    ...(liftComparisonData ? {liftComparisonData} : {}),
  };
}

export function getSeriesKeys(insightsTree: InsightsSeriesTree): string[][] {
  const depth = nestedObjectDepthInsightsFormat(insightsTree);
  return new Array(depth).fill(null).map((_, idx) => nestedObjectKeys(insightsTree, depth - idx + 1)) as string[][];
}

export function getQueryModeRowTypes({
  canUseCohorts,
  isSingleReport,
  formulasDisableReason,
}: {
  canUseCohorts: boolean;
  isSingleReport: boolean;
  formulasDisableReason?: string;
}): RowTypeOption[] {
  return [
    ResourceType.Event,
    canUseCohorts && ResourceType.Cohort,
    {value: ResourceType.User, placeholderEntry: {event: TOP_PEOPLE}},
    ...(isSingleReport ? [MetricType.Funnel, MetricType.Retention, MetricType.RetentionFrequency] : []),
    {
      value: ResourceType.Formula,
      ...(formulasDisableReason && {disabledReason: formulasDisableReason}),
    },
  ].filter(Boolean);
}

/**
 * Looks up the specified segment, accounting for multi-metric reports.
 * When looking up a comparison segment in a main segmentLookup, pass in the full segment path of the main segment.
 * @param {string} segmentLookupKey whatever segment key you have in hand.
 * @param {Map<string,FlatSegment>} segmentLookup the map of all segments in the report
 * @param {string[]} segmentPath optional - if this is a comparison lookup, pass in the segment path of the "this" segment (i.e. the segment being compared to baseline, or overall)
 */
export function lookupSegment<TValue = number>(
  segmentLookupKey: string,
  segmentLookup: Map<string, FlatSegment<TValue>>,
  segmentPath?: string[],
): FlatSegment<TValue> {
  if (segmentLookup.has(segmentLookupKey)) {
    // single metric
    return segmentLookup.get(segmentLookupKey);
  } else {
    // multi-metric
    const rebasedLookupKey = [segmentPath.slice(0, -1), segmentLookupKey].join(`,`);
    return segmentLookup.get(rebasedLookupKey);
  }
}

export function shouldShowSeriesTotal(sections: InsightsBookmarkSections): boolean {
  if (sections.group?.some((clause) => clause.cohorts && clause.cohorts?.length)) {
    return false;
  }

  if (getBehaviorShowClauses(sections.show).some((clause) => clause.measurement.math !== PRIMITIVE_MATH_TYPES.TOTAL)) {
    return false;
  }

  return !sections.group?.some((clause) => clause.propertyType === PropertyType.List);
}

/*
  Determines if this is an appropriate chart to show "Other" in. Does not consider the chart data.
*/
export function canShowOtherSegment(params: InsightsChartParams): boolean {
  const isLiftPercentageOfOverall = params.liftComparison?.type === LiftType.Overall;
  const isPercentageOfOverall = params.displayOptions.value === ValueRepresentationType.Relative;
  const displayOptions = getDisplayOptions(params.sections, params.displayOptions);
  const isStacked = displayOptions.plotStyle === ChartPlotStyle.Stacked;
  const isPie = displayOptions.chartType === ChartType.Pie;
  const isLine = displayOptions.chartType === ChartType.Line;
  const isTotal = shouldShowSeriesTotal(params.sections);

  return (
    (isPie && isTotal) ||
    (isStacked && isPercentageOfOverall && isTotal) ||
    (isLiftPercentageOfOverall && isStacked && isLine)
  );
}

export function isEmptyResult(result: InsightsQueryResults): boolean {
  const isEmptySeries = result && Object.keys(result.series).length === 0;
  // Occurs when multiple events are selected along with "compare to overall" (without breakdowns)
  const isEmptyOverallComparisonResult = result && isEqual({$overall: {}}, result.series);
  const isEmptySegResult = result && isEmptySegmentationResult(result);

  return isEmptySeries || isEmptyOverallComparisonResult || isEmptySegResult;
}

export function isEmptyRetentionResult(result: InsightsQueryResults): boolean {
  const retentionResults = result && result.series && Object.values(result.series)[0];
  const retentionPastResults =
    result &&
    result.time_comparison &&
    result.time_comparison.series &&
    Object.values(result.time_comparison.series)[0];

  return !hasData(retentionResults) && !hasData(retentionPastResults);

  function hasData(currData) {
    if (Array.isArray(currData)) {
      return currData?.some((num) => num > 0);
    }
    for (const key in currData) {
      if (hasData(currData[key])) {
        return true;
      }
    }
    return false;
  }
}

export function isEmptyFunnelResult(result: InsightsQueryResults, params: InsightsBookmarkParams): boolean {
  const funnelResults = result && result.series && Object.values(result.series)[0];
  const funnelPastResults =
    result &&
    result.time_comparison &&
    result.time_comparison.series &&
    Object.values(result.time_comparison.series)[0];
  const traverseLevel = params.sections.group.length + getNumerOfEventComparisons(params.sections.show[0]);

  return !hasData(funnelResults, traverseLevel, 0) && !hasData(funnelPastResults, traverseLevel, 0);

  function hasData(currData, targetDepth, currDepth) {
    if (currDepth === targetDepth) {
      for (const key in currData) {
        if (key === `count`) {
          for (const leafKey in currData[key]) {
            const leafValue = currData[key][leafKey]?.all;
            if (leafValue > 0 && leafValue !== null) {
              return true;
            }
          }
        }
      }
      return false;
    }
    for (const key in currData) {
      if (hasData(currData[key], targetDepth, currDepth + 1)) {
        return true;
      }
    }
    return false;
  }
}

function isValidFunnelMetricShowClause(showClause: ShowClauseType): boolean {
  if (!isBehaviorShowClause(showClause)) {
    return false;
  }
  const allBehaviorsHaveName = showClause.behavior.behaviors?.every((behavior) => {
    if (behavior.behaviors) {
      return behavior.behaviors.every((beh) => !!beh.name);
    } else {
      return !!behavior.name;
    }
  });

  return showClause.behavior.behaviors?.length > 1 && allBehaviorsHaveName;
}

function isValidRetentionMetricShowClause(showClause: ShowClauseType): boolean {
  if (!isBehaviorShowClause(showClause)) return false;
  const allBehaviorsHaveName = showClause.behavior.behaviors?.every((behavior) => !!behavior.name);

  return showClause.behavior.behaviors?.length === 2 && allBehaviorsHaveName;
}

function isValidRetentionFrequencyMetricShowClause(showClause: ShowClauseType): boolean {
  if (!isBehaviorShowClause(showClause)) return false;
  const allBehaviorsHaveName = showClause.behavior.behaviors?.every((behavior) => !!behavior.name);

  return showClause.behavior.behaviors?.length === 1 && allBehaviorsHaveName;
}

// TODO: make this function to work properly when insight, funnel and retention metric at the same time
export function hasValidMetricBookmarkParams(showClauses: ShowClauseType[]) {
  const hasFunnelsQuery = showClauses.some(
    (showClause) => isBehaviorShowClause(showClause) && showClause.behavior.type === MetricType.Funnel,
  );
  if (hasFunnelsQuery) {
    // need to make sure nested behavior is allowed.
    const funnelMetricShowClauses = showClauses.filter(
      (showClause) => isBehaviorShowClause(showClause) && showClause.behavior.type === MetricType.Funnel,
    );

    return funnelMetricShowClauses.every((showClause) => isValidFunnelMetricShowClause(showClause));
  }
  const hasRetentionQuery = showClauses.some(
    (showClause) => isBehaviorShowClause(showClause) && showClause.behavior.type === MetricType.Retention,
  );
  if (hasRetentionQuery) {
    // need to make sure nested behavior is allowed.
    const retentionMetricShowClauses = showClauses.filter(
      (showClause) => isBehaviorShowClause(showClause) && showClause.behavior.type === MetricType.Retention,
    );

    return retentionMetricShowClauses.every((showClause) => isValidRetentionMetricShowClause(showClause));
  }
  const hasRetentionFrequencyQuery = showClauses.some(
    (showClause) => isBehaviorShowClause(showClause) && showClause.behavior.type === MetricType.RetentionFrequency,
  );
  if (hasRetentionFrequencyQuery) {
    // need to make sure nested behavior is allowed.
    const retentionFrequencyMetricShowClauses = showClauses.filter(
      (showClause) => isBehaviorShowClause(showClause) && showClause.behavior.type === MetricType.RetentionFrequency,
    );

    return retentionFrequencyMetricShowClauses.every((showClause) =>
      isValidRetentionFrequencyMetricShowClause(showClause),
    );
  }
  return (
    Boolean(
      Array.isArray(showClauses) === false ||
        showClauses.length === 0 ||
        showClauses.some((clause) => {
          if (isFormulaShowClause(clause)) {
            return !isValidFormula(clause.formula);
          } else {
            return !clause.behavior.name && !clause.behavior.id && !clause.behavior.raw_cohort;
          }
        }),
    ) === false
  );
}

//An empty segmentation result is when a Value Comparison query has no values with valid data and results in a series with just the events and the $overall tag
export function isEmptySegmentationResult(result: InsightsQueryResults): boolean {
  return Object.values(result.series).every(
    (data) => Object.keys(data).length === 1 && Object.keys(data)[0] === `$overall`,
  );
}

export function hasMoreThanOneTimeValue(result: InsightsQueryResults) {
  return getTimestamps(result.series).length > 1;
}

export function insightsBookmarkParamsToChartParams(params: InsightsBookmarkParams): InsightsChartParams {
  return pick(params, [`sections`, `displayOptions`, `liftComparison`]);
}

export function getResultsHeadersWithoutGroup(originalResultHeaders: string[], group: GroupClause): string[] {
  // When displayOptions.chartType is "line", the last breakdown-by-date in a people query isn't formatted like a normal breakdown in query results,
  // query-API will actually format it like a timeseries. That's why we omit the last breakdown from the header in this case.
  const headerToRemoveIdx = originalResultHeaders.lastIndexOf(group.value as string);
  if (headerToRemoveIdx !== -1) {
    return originalResultHeaders.filter((_, idx) => idx !== headerToRemoveIdx);
  }

  return originalResultHeaders;
}

export function checkIsMultiMetricBookmark(showClauses: ShowClauseType[]) {
  return showClauses.some((showClause) => `behavior` in showClause && `measurement` in showClause);
}

// used to populate custom alert modal
export function getResultsKeysAndValues(
  params: InsightsBookmarkParams,
  result: InsightsQueryResults,
  dataGroups: DataGroup[],
): ResultsKeysAndValues[] {
  const isMultiMetricBookmark = checkIsMultiMetricBookmark(params.sections.show);
  const isFunnelsStepsChart = params.displayOptions.chartType === ChartType.FunnelSteps;

  const seriesKeys = getSeriesKeys(result.series);

  const isLineChart = getDisplayOptions(params.sections, params.displayOptions).chartType === ChartType.Line;

  // When displayOptions.chartType is "line", the last breakdown-by-date in a people query isn't formatted like a normal breakdown in query results,
  // query-API will actually format it like a timeseries. That's why we omit the last breakdown from group clauses and headers in this special case.
  const shouldOmitLastGroup = isLineChart && isPeopleTimeSeries(params.sections);
  const bookmarkSections = shouldOmitLastGroup
    ? {...params.sections, group: params.sections.group.slice(0, -1)}
    : params.sections;
  const resultsHeaders = shouldOmitLastGroup
    ? getResultsHeadersWithoutGroup(result.headers, params.sections.group[params.sections.group.length - 1])
    : result.headers;

  const displayHeaders = getSeriesDisplayHeaders(bookmarkSections, resultsHeaders, keyBy(dataGroups, `groupID`));
  // Pre select the selected count type and the last step for multi metric funnel steps chart
  if (isMultiMetricBookmark && isFunnelsStepsChart) {
    processSeriesKeysForMultiMetricFunnelsSteps(seriesKeys, displayHeaders, bookmarkSections);
  }

  return zip(seriesKeys, resultsHeaders, displayHeaders).map(([keys, header, displayHeader]) => {
    return {
      name: header,
      displayName: displayHeader,
      /** $overall is a new key that's being returned from the Insights API. Custom Alerts was not handling
       * this new change thus throwing errors. Filter here basically blocks the use of $overall in Custom Alerts
       * creation given it's an invalid input.*/
      displayValues: keys?.map((key) => renamePropertyValue(key, header)).filter((value) => value !== `$overall`),
      values: keys?.filter((value) => value !== `$overall`),
    };
  });
}

function processSeriesKeysForMultiMetricFunnelsSteps(
  seriesKeys: string[][],
  displayHeaders: string[],
  bookmarkSections: InsightsBookmarkSections,
) {
  // Get index of "Metric" header
  const metricHeaderIdx = displayHeaders.findIndex((header) => header === `Metric`);
  const stepHeaderIdx = displayHeaders.findIndex((header) => header === `Step`);
  const countType = bookmarkSections.show[0][`measurement`][`math`];
  // filter seriesKeys based on the index of "Metric" header
  seriesKeys[metricHeaderIdx] = seriesKeys[metricHeaderIdx].filter(
    (key) => key === MATH_TO_FUNNEL_STEPS_METRIC[countType],
  );
  // filter seriesKeys based on the index of "Step" header
  seriesKeys[stepHeaderIdx] = seriesKeys[stepHeaderIdx].slice(seriesKeys[stepHeaderIdx].length - 1);
}

export function getQuerySamplingFactor(results: InsightsQueryResults) {
  // Validate min_sampling_factor or return 100 (not sampled)
  if (!results?.meta || !results.meta.min_sampling_factor || results.meta.min_sampling_factor < 0) {
    return 1;
  }

  return results.meta.min_sampling_factor;
}

// from: https://stackoverflow.com/questions/19752516/renaming-object-keys-recursively
const objectRecursiveKeyMap = (obj, fn) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      const getValue = (v) => (typeof v === `object` && v !== null ? objectRecursiveKeyMap(v, fn) : v);
      return [fn(key), Array.isArray(value) ? value.map((val) => getValue(val)) : getValue(value)];
    }),
  );

export function modifyDateRangeKeys(series: InsightsSeriesTree, hourModifier: number) {
  const hourToMilliseconds = hourModifier * 60 * 60 * 1000;
  return objectRecursiveKeyMap(series, (key) => {
    if (Date.parse(key)) {
      const newTimestamp = new Date(key).getTime() + hourToMilliseconds;
      return new Date(newTimestamp).toISOString();
    }
    return key;
  });
}

export function getNumShowClausesWithMultiAttribution(showClauses: ShowClause[]): number {
  return showClauses.filter((c: ShowClauseClassType) => !!c.measurement.multiAttribution).length;
}

export function getMultiAttributionClauses(groupClauses: GroupClause[]) {
  return groupClauses.filter((c) => isMultiAttributionBehavior(c.behavior));
}

export function getMultiAttributionProperty(groupClauses: GroupClause[]): Segmentation | null {
  const multiAttributionClauses = getMultiAttributionClauses(groupClauses);
  return multiAttributionClauses.length ? getSelectedSegments(multiAttributionClauses)[0][ResourceType.Event] : null;
}

export function getNumMultiAttributionProps(groupClauses: GroupClause[]): number {
  return getMultiAttributionClauses(groupClauses).length;
}

export function hasOnlyEventBasedMetrics({
  showClauses,
  metricsResource,
}: {
  showClauses: ShowClause[];
  metricsResource: MetricsResource;
}): boolean {
  return showClauses.every((clause: ShowClauseClassType) => {
    if (clause instanceof FormulaShowClause) {
      const formulaShowClause = clause.toBookmarkData();
      if (formulaShowClause.formula.id) {
        const resolvedFormula = getResolvedFormulaShowClause({
          formulaShowClause: clause.toBookmarkData(),
          metricsResource,
        });
        return (
          !resolvedFormula ||
          resolvedFormula.formula.referencedMetrics.every(
            (clause) => clause.behavior.resourceType === InsightsResourceType.Events,
          )
        );
      } else {
        return true;
      }
    } else {
      return clause.behavior.resourceType === InsightsResourceType.Events;
    }
  });
}

export function groupEventComparisonShowMetadata(showMetadata) {
  const flattenedShowMetadata = [];
  let tempEventComparisonHolder = [];
  if (!showMetadata || !Array.isArray(showMetadata)) {
    return [];
  }
  for (const metadata of showMetadata) {
    if (metadata[`is_event_comparison`]) {
      tempEventComparisonHolder.push(metadata);
    } else {
      // processed all is_event_comparison, push the result to the list
      // we can rely on this logic since event comaparison metrics area always next to each other.
      if (tempEventComparisonHolder.length) {
        flattenedShowMetadata.push(tempEventComparisonHolder);
        tempEventComparisonHolder = [];
      }
      flattenedShowMetadata.push(metadata);
    }
  }
  if (tempEventComparisonHolder.length) {
    flattenedShowMetadata.push(tempEventComparisonHolder);
  }
  return flattenedShowMetadata;
}
